import { DEFAULT_STARTER_CHATBOT_CONFIG, type StarterChatbotConfigValue } from './internal/aiConfigStarterChatbot';
import { type AiLibraryPageSizes, DEFAULT_AI_LIBRARY_PAGE_SIZES } from './internal/aiLibraryPageSizes';
import { createFlagFunction } from './internal/createFlagFunction';
import {
  DEFAULT_RELEASE_CONFIGURATION_OFF,
  type DefaultReleaseConfiguration,
} from './internal/defaultReleaseConfiguration';
import {
  defaultFeatureMatrixValue,
  warehouseWithDataExportSupportDefaultValue,
} from './internal/productAnalytics/defaultValue';
import type { ProductAnalyticsFeatureMatrix } from './internal/productAnalytics/types';

export { DEFAULT_STARTER_CHATBOT_CONFIG } from './internal/aiConfigStarterChatbot';
export type { StarterChatbotConfigValue, StarterChatbotConfigVariation } from './internal/aiConfigStarterChatbot';
export { DEFAULT_AI_LIBRARY_PAGE_SIZES } from './internal/aiLibraryPageSizes';
export type { AiLibraryPageSizes } from './internal/aiLibraryPageSizes';
export type {
  DefaultReleaseConfiguration,
  DefaultReleaseConfigurationEnvironment,
  DefaultReleaseConfigurationOverride,
} from './internal/defaultReleaseConfiguration';

export const acceptedRedirectHosts = createFlagFunction<string[]>('accepted-redirect-hosts', []);
export const accessTokenExpiryNotificationConfiguration = createFlagFunction<{ notifyDaysFromExpiry: number[] }>(
  'access-token-expiry-notification-configuration',
  { notifyDaysFromExpiry: [] },
);
export const addMccAdjustedFrequentistPopover = createFlagFunction('add-mcc-adjusted-frequentist-popover', false);
export const addMeanToForestPlotPopover = createFlagFunction('add-mean-to-forest-plot-popover', false);
export const aeFactoryDogfoodTest = createFlagFunction('ae-factory-dogfood-test', false);
export const aeFactoryDogfoodTest2 = createFlagFunction('ae-factory-dogfood-test-2', false);
export const agentControlSignupStraightToConfigs = createFlagFunction(
  'agent-control-signup-straight-to-configs',
  false,
);
export const agentControlStarterChatbotConfig = createFlagFunction<StarterChatbotConfigValue>(
  'agent-control-starter-chatbot-config',
  DEFAULT_STARTER_CHATBOT_CONFIG,
);
export const agentControlDismissibleAdExperiment2 = createFlagFunction<'exploreAgentControl' | 'createConfig'>(
  'agentcontrol-dismissible-ad-experiment-2',
  'exploreAgentControl',
);
export const agentControlLlmDashboardBannerExperiment = createFlagFunction<
  'catchRegressions' | 'fixInMinutes' | 'measureThenImprove'
>('agentcontrol-llm-dashboard-banner-experiment', 'catchRegressions');
export const agentControlSdkSetupCopyLinkExperiment = createFlagFunction(
  'agentcontrol-sdk-setup-copy-link-experiment',
  false,
);
export const agentControlOneClickAgentButtons = createFlagFunction('agent-control-one-click-agent-buttons', false);
export const agentControlWelcomeCopyExperiment = createFlagFunction('agent-control-welcome-copy-experiment', false);
export const agentSkillsMaxMarkdownKb = createFlagFunction('agent-skills-max-markdown-kb', 50);
export const agenticOnboardingSnippetText = createFlagFunction<string>(
  'agentic-onboarding-snippet-text',
  'Run `npx skills add launchdarkly/ai-tooling --skill onboarding -y` and onboard me to LaunchDarkly',
);
export const aiConfigJsonVariationPayload = createFlagFunction('enable-ai-config-json-variation-payload', 128);
export const aiConfigUsageGateTrials = createFlagFunction<{
  hardLimit: number;
  softLimit: number;
  variationLimit: number;
}>('ai-config-usage-gate-trials', { hardLimit: 0, softLimit: 0, variationLimit: 2 });
export const aiLibraryPageSizes = createFlagFunction<Partial<AiLibraryPageSizes>>(
  'ai-library-page-sizes',
  DEFAULT_AI_LIBRARY_PAGE_SIZES,
);
export const allowChartFilterExperience = createFlagFunction('chart-filter-experience', false);
export const allowDecouplingUnitOfAnalysisForRg = createFlagFunction('allow-decoupling-unit-of-analysis-for-rg', false);
export const allowLinkingAiConfigsToViews = createFlagFunction('allow-linking-ai-configs-to-views', true);
export const allowLinkingFlagsToViews = createFlagFunction('allow-linking-flags-to-views', true);
export const allowLinkingMetricsToViews = createFlagFunction('allow-linking-metrics-to-views', true);
export const allowLinkingSegmentsToViews = createFlagFunction('allow-linking-segments-to-views', true);
export const allowReenablingFailedTriggers = createFlagFunction('allow-reenabling-failed-triggers', false);
export const allowViewLinkingResourcesViaFilters = createFlagFunction('allow-view-linking-resources-via-filters', true);
export const announcementPollingIntervalSeconds = createFlagFunction('announcement-polling-interval-seconds', 0);
export const approvalsNavPendingCountPollIntervalMs = createFlagFunction(
  'approvals-nav-pending-count-poll-interval-ms',
  0,
);
export const approvalsNotificationListMaxLimit = createFlagFunction('approvals-notification-list-max-limit', 100);
export const areAgenticOnboardingPromptUpdatesEnabled = createFlagFunction('agentic-onboarding-prompt-updates', false);
export const awsDevOpsAgentSdkSetup = createFlagFunction('aws-devops-agent-sdk-setup', false);
export const arePayloadFiltersAtFlagCreationEnabled = createFlagFunction(
  'show-payload-filters-at-flag-creation',
  false,
);
export const areRelayAutoConfigsEnabled = createFlagFunction('enable-relay-auto-configs', false);
export const areServiceTokensEnabled = createFlagFunction('enable-service-tokens', false);
export const areSignupsDisallowed = createFlagFunction('disallow-signups', false);
export const areWorkflowTemplatesEnabled = createFlagFunction('enable-workflow-templates', true);
export const audiencePhasesMaximumPhaseLimit = createFlagFunction('audience-phases-maximum-phase-limit', 10);
export const audiencePhasesMaximumPhaseStepLimit = createFlagFunction('audience-phases-maximum-phase-step-limit', 10);
export const auditLogHistoryLimit = createFlagFunction('enable-audit-log-history-length', -1);
export const beastModeOptions = createFlagFunction<{ all: boolean; routes: string[] }>('beast-mode', {
  all: false,
  routes: [],
});
export const beastModeProfilePageRevamp = createFlagFunction('beast-mode-profile-page-revamp', false);
export const bigQueryServiceAccountEmail = createFlagFunction('bigquery-service-account-email', '');
export const bigSegmentsImportMaxRecords = createFlagFunction('big-segments-import-max-records', 0);
export const blockModifyInUseVariations = createFlagFunction('block-modify-in-use-variations', false);
export const cleanUpRoleAttributesAndViews = createFlagFunction('clean-up-role-attributes-and-views', false);
export const clearMissingRecentProjectKey = createFlagFunction('clear-missing-recent-project-key', false);
export const commandPaletteSnapPrefix = createFlagFunction<string>('command-palette-snap-prefix', 'in:');
export const concurrentSdkKeysFe = createFlagFunction('concurrent-sdk-keys-fe', false);
export const configurableDestinations = createFlagFunction<string[]>('configurable-destinations', []);
export const configureBranchFilter = createFlagFunction('configure-coderefs-branch-ux-filter', 0);
export const contactUsSeatLimit = createFlagFunction('contact-us-seat-limit', -1);
export const contextKeyCellDetailsPopover = createFlagFunction('context-key-cell-details-popover', false);
export const cooloffRetrialDurationDays = createFlagFunction('cooloff-retrial-duration-days', 14);
export const cooloffRetrialExtensionDurationDays = createFlagFunction('cooloff-retrial-extension-duration-days', 14);
export const createFlagMenuPrimaryClick = createFlagFunction('create-flag-menu-primary-click', false);
export const crossProductExpansionArms = createFlagFunction<'control' | 'observability' | 'experimentation'>(
  'cross-product-expansion-arms',
  'control',
);

export const cutoffDateForIterationStartForCHEX = createFlagFunction('iteration-started-at-chex-cutoff-timestamp', 0);
export const datadogRUMPremiumSampleRate = createFlagFunction(
  'web-app-data-dog-rum-application-premium-sample-rate',
  0,
);
export const datadogRUMSampleRate = createFlagFunction('web-app-data-dog-rum-application-sample-rate', 0);
export const datasetMaxFileSizeBytes = createFlagFunction('ai-evaluator-dataset-max-file-size-bytes', 32 * 1024 * 1024); // 32MB
export const debuggerFullFidelityEventDurationMinutes = createFlagFunction(
  'debugger-full-fidelity-events-duration-in-minutes',
  30,
);

export const defaultReleaseConfiguration = createFlagFunction<DefaultReleaseConfiguration>(
  'default-release-configuration',
  DEFAULT_RELEASE_CONFIGURATION_OFF,
);

export const disableBuiltInContextNameAttributes = createFlagFunction(
  'disable-built-in-context-name-attributes',
  false,
);
export const disableClickhouseDependencies = createFlagFunction('disable-clickhouse-dependencies', false);
export const disablecMAUChartAndDisplayMessage = createFlagFunction('disable-cmau-chart-and-display-message', '');
export const disabledRules = createFlagFunction('disable-rules', false);
export const disableFreeDevPlanSelection = createFlagFunction('disable-free-dev-plan-selection', false);
export const disableObservabilitySessions = createFlagFunction('disable-o11y-sessions-view', false);
export const disableSegmentExpandFlags = createFlagFunction('disable-segment-expand-flags', false);
export const disableWithProjectContext = createFlagFunction('disable-withprojectcontext', false);
export const disallowNewAttributeValues = createFlagFunction('disallow-new-attribute-values', false);
export const displayAdaptiveTriggers = createFlagFunction('display-adaptive-triggers', false);
export const displayCupedV1DeprecationWarningBanner = createFlagFunction(
  'display-cuped-v-1-deprecation-warning-banner',
  false,
);
export const displayOptOutCheckboxForGenAiFeatures = createFlagFunction(
  'display-opt-out-checkbox-for-gen-ai-features',
  false,
);
export const displayRequestTriggersAccessModal = createFlagFunction('display-adaptive-triggers-request-access', false);
export const displaySupportGenerativeAiOptInUi = createFlagFunction('display-support-generative-ai-opt-in-ui', false);

export const editableApprovalRequestTitles = createFlagFunction('editable-approval-request-titles', false);
export const eMAUSelfServePricingFacade = createFlagFunction<{
  freeQuantity?: number;
  overagePriceInCents?: number;
  showOveragePrice?: boolean;
}>('emau-self-serve-pricing-facade', {});
export const enableAaTestGuidanceAlerts = createFlagFunction('enable-aa-test-guidance-alerts', false);
export const enableAccentInsensitiveSearch = createFlagFunction('enable-accent-insensitive-search', false);
export const enableAccessCheckFilterOnGetMembers = createFlagFunction(
  'enable-access-check-filter-on-get-members',
  false,
);
export const enableAccessCheckFilterOnGetTeams = createFlagFunction('enable-access-check-filter-on-get-teams', false);
export const enableAccessSummaryUI = createFlagFunction('enable-access-summary-ui', false);
export const enableAccessSummaryProseAI = createFlagFunction('enable-access-summary-prose-ai', false);
export const enableAccessTokenExpiryNotificationsSetting = createFlagFunction(
  'enable-access-token-expiry-notifications-setting',
  false,
);
export const enableAccountHasConnectedSdk = createFlagFunction('enable-account-has-connected-sdk', false);
export const enableAdaptiveTriggersForAiConfigs = createFlagFunction('enable-adaptive-triggers-for-ai-configs', false);
export const enableAdaptiveTriggersForFlags = createFlagFunction('enable-adaptive-triggers-for-flags', false);
export const enableAddTargetingFromSegmentOverview = createFlagFunction('add-targeting-from-segment-overview', false);
export const enableAgencontrolModelCostEstimator = createFlagFunction('enable-agencontrol-model-cost-estimator', false);
export const enableAgentControlCheckout = createFlagFunction('enable-agent-control-checkout', false);
export const enableAgentControlFunnelInstrumentation = createFlagFunction(
  'enable-agentcontrol-funnel-instrumentation',
  false,
);
export const enableAgentGraphSearch = createFlagFunction('enable-agent-graph-search', false);
export const enableAgentHelpButton = createFlagFunction('enable-agent-help-button', false);
export const enableAgentOptimization = createFlagFunction('enable-agent-optimization', false);
export const enableAgentOptimizationMetadata = createFlagFunction('enable-agent-optimization-metadata', false);
export const enableAgentOptimizationSearch = createFlagFunction('enable-agent-optimization-search', false);
export const enableAgentOptimizationVariationKey = createFlagFunction('enable-agent-optimization-variation-key', false);
export const enableAIAnnotations = createFlagFunction('enable-ai-annotations', false);
export const enableAIBundle = createFlagFunction('enable-ai-bundle', false);
export const enableAicAgentSkills = createFlagFunction('enable-aic-agent-skills', false);
export const enableAICJudgeCustomizing = createFlagFunction('enable-aic-judge-customizing', false);
export const enableAiConfigStandaloneJudgeMetrics = createFlagFunction(
  'enable-ai-config-standalone-judge-metrics',
  false,
);
export const enableAICJudgeLibraryTab = createFlagFunction('enable-aic-judge-library-tab', false);
export const enableAiConfigAgentGraphs = createFlagFunction('enable-ai-config-agent-graph', false);
export const enableAiConfigAutoSampleOnboarding = createFlagFunction('enable-ai-config-auto-sample-onboarding', false);
export const enableAiConfigChartDiagnostics = createFlagFunction('enable-ai-config-chart-diagnostics', false);
export const enableAiConfigClone = createFlagFunction('enable-ai-config-clone', false);
export const enableAiConfigCustomMetricMonitoring = createFlagFunction(
  'enable-ai-config-custom-metric-monitoring',
  false,
);
export const enableAiConfigJudgesForAgents = createFlagFunction('enable-ai-config-judges-for-agents', false);
export const enableAiConfigMetricsContextFilter = createFlagFunction('enable-ai-config-metrics-context-filter', false);
export const enableAiConfigMetricsQueryBuilder = createFlagFunction('enable-ai-config-metrics-query-builder', false);
export const enableAiConfigModeTabs = createFlagFunction('enable-ai-config-mode-tabs', false);
export const enableAiConfigMonitoringDeeplinks = createFlagFunction('enable-ai-config-monitoring-deeplinks', false);
export const enableAiConfigMonitoringMultiMetricCharts = createFlagFunction(
  'enable-ai-config-monitoring-multi-metric-charts',
  false,
);
export const enableAiConfigNthUserSampleCta = createFlagFunction('enable-ai-config-nth-user-sample-cta', false);
export const enableAiConfigOnboarding = createFlagFunction('enable-ai-config-onboarding', false);
export const enableAiConfigOutputFormat = createFlagFunction('enable-ai-config-output-format', false);
export const enableAiConfigVariationServingStatus = createFlagFunction(
  'enable-ai-config-variation-targeting-status',
  false,
);
export const enableArrayTypeSelectionInSchemaBuilder = createFlagFunction(
  'enable-array-type-selection-in-schema-builder',
  false,
);
export const enableAiConfigsCostMetrics = createFlagFunction('enable-ai-configs-cost-metrics', false);
export const enableCustomModelVersioning = createFlagFunction('enable-custom-model-versioning', false);
export const enableAiConfigsModelBulkVersionUpdate = createFlagFunction(
  'enable-ai-configs-model-bulk-version-update',
  false,
);
export const enableAiConfigsModelBulkSwap = createFlagFunction('enable-ai-configs-model-bulk-swap', false);
export const enableAiConfigsEvaluationListView = createFlagFunction('evaluations-list-view', false);
export const enableAiConfigsEvaluationMoonshot = createFlagFunction('enable-eval-moonshot', false);
export const enableAiConfigsGlobalProviders = createFlagFunction('enable-global-ai-models-with-provider', false);
export const enableAIConfigsJudgeEvals = createFlagFunction('enable-ai-config-judge-evaluations', false);
export const enableAiConfigsPromptOptimizer = createFlagFunction('enable-prompt-optimization-features', false);
export const enableAiConfigsTestRun = createFlagFunction('enable-ai-configs-test-run', false);
export const enableAiConfigsTraces = createFlagFunction('enable-ai-config-traces', false);
export const enableAiConfigTryItLive = createFlagFunction('enable-ai-config-try-it-live', false);
export const enableAIConfigWaitlistPage = createFlagFunction('ai-config-waitlist', false);
export const enableAiMonitoringInsights = createFlagFunction('enable-ai-monitoring-insights', false);
export const enableAiMonitoringInsightsContextGrouping = createFlagFunction(
  'enable-ai-monitoring-insights-context-grouping',
  false,
);
export const enableAiMonitoringVega = createFlagFunction('enable-ai-monitoring-vega', false);
export const enableAiSdkVersionsInSdkConnections = createFlagFunction(
  'enable-ai-sdk-versions-in-sdk-connections',
  false,
);
export const enableAIRunsCheckoutDisplay = createFlagFunction('enable-ai-runs-checkout-display', false);

export const enableAIRunsUsageBanner = createFlagFunction('enable-ai-runs-usage-banner', false);
export const enableNonAdminTokenScopeHints = createFlagFunction('enable-non-admin-token-scope-hints', false);
export const enableRouteUsageBanners = createFlagFunction('enable-route-usage-banners', false);
export const enableAiToolCustomParams = createFlagFunction('enable-ai-tool-custom-params', false);
export const enableAiToolVersionHistory = createFlagFunction('enable-ai-tool-version-history', false);
export const enableAnnouncementBanner = createFlagFunction('enable-announcement-banner', false);
export const enableApiVersionTokensFilter = createFlagFunction('enable-api-version-tokens-filter', false);
export const enableApprovalDiffEpochTooltips = createFlagFunction('enable-approval-diff-epoch-tooltips', false);
export const enableSegmentApprovalFullBaselineDiff = createFlagFunction(
  'enable-segment-approval-full-baseline-diff',
  false,
);
export const enableApprovalRequestClientSidePolling = createFlagFunction(
  'enable-approval-request-client-side-polling',
  false,
);
export const enableApprovalRequestEmptyTeamReviewerGuard = createFlagFunction(
  'enable-approval-request-empty-team-reviewer-guard',
  false,
);
export const enableApprovalsNavPendingCount = createFlagFunction('enable-approvals-nav-pending-count', false);
export const enableApplicationViews = createFlagFunction('enable-application-views', false);
export const enableApplicationViewAutoLinking = createFlagFunction('enable-application-view-auto-linking', false);
export const enableAppsAndAppVersions = createFlagFunction('enable-apps-and-versions', true);
export const enableArchivingFlagThatIsPrerequisite = createFlagFunction(
  'enable-archiving-flag-that-is-prerequisite',
  false,
);
export const enableAssignedAccessRedesign = createFlagFunction('enable-assigned-access-redesign', false);
export const enableAttributeDataSourceCreation = createFlagFunction('enable-attribute-data-source-creation', false);
export const enableAttributeDataSourcesForExperiments = createFlagFunction(
  'enable-attribute-data-sources-for-experiments',
  false,
);
export const enableAudiencePhaseReleases = createFlagFunction('enable-audience-phase-releases', false);
export const enableAutomatedFlagCleanup = createFlagFunction('enable-automated-flag-cleanup', false);
export const enableAutomatedFlagCleanupUnlimited = createFlagFunction('enable-automated-flag-cleanup-unlimited', false);
export const enableAutoSeatProvisioning = createFlagFunction('enable-auto-seat-provisioning', false);
export const enableAwsMarketplaceLinking = createFlagFunction('enable-aws-marketplace-linking', false);
export const enableAwsMarketplaceSignup = createFlagFunction('enable-aws-marketplace-signup', false);
export const enableBayesianPriorSelection = createFlagFunction('enable-bayesian-prior-selection', false);
export const enableBillingPage = createFlagFunction('enable-billing-page', false);
export const enableBizibleScript = createFlagFunction('enable-bizible-script', false);
export const enableBlockDragAndDrop = createFlagFunction('show-drag-drop-chart-placeholder', false);
export const enableBlueCreateMetricButton = createFlagFunction('enable-blue-create-metric-button', false);
export const enableBundlerImageImports = createFlagFunction('enable-bundler-image-imports', false);
export const enableCachedInputTokenCostField = createFlagFunction('enable-cached-input-token-cost-field', false);
export const enableCachedLoader = createFlagFunction('cached-loader', false);
export const enableCanaryToken = createFlagFunction('enable-canary-token', false);
export const enableCancelReason = createFlagFunction('enable-cancel-reason', false);
export const enableChangeHistory = createFlagFunction('enable-change-history', false);
export const enableChangeHistoryAgentAttribution = createFlagFunction('enable-change-history-agent-attribution', false);
export const enableChangeHistoryMemberFilter = createFlagFunction('enable-change-history-member-filter', false);
export const enableChartSQLView = createFlagFunction('chart-sql-view', false);
export const enableCheckoutSeedExistingCommits = createFlagFunction('checkout-seed-existing-commits', false);
const contextsInEwaBackend = createFlagFunction('use-contexts-in-ewa', 'disabled');
export const enableClickhouseContextsApiChangesInTheUi = () =>
  contextsInEwaBackend() === 'enabled' || contextsInEwaBackend() === 'enabled-session-auth-only';
export const enableClickHouseDataExport = createFlagFunction('enable-click-house-data-export', false);
export const enableClickHouseNativeExperimentationTwoStepSetup = createFlagFunction(
  'enable-click-house-native-experimentation-two-step-setup',
  false,
);
export const enableClientSideConfigurationByDefault = createFlagFunction(
  'enable-client-side-configuration-by-default',
  false,
);
export const enableCmdKNewNavigation = createFlagFunction('enable-cmd-k-new-navigation', false);
export const enableCollectCompanyNameOnWelcome = createFlagFunction('collect-company-name-on-welcome', false);
export const enableCommandPalette = createFlagFunction('command-palette', false);
export const enableCommandPaletteFlagCommands = createFlagFunction('command-palette-flag-commands', false);
export const enableCommandPaletteViewGiveFeedback = createFlagFunction('command-palette-view-give-feedback', false);
export const enableCommandPaletteViewSaveShortcut = createFlagFunction('command-palette-view-save-shortcut', false);
export const enableConfigDrivenPlanCatalog = createFlagFunction('enable-config-driven-plan-catalog', false);
export const enableConfigurableExperimentationForProPlans = createFlagFunction(
  'enable-configurable-experimentation-for-pro-plans',
  false,
);
export const enableConfigWizard = createFlagFunction('enable-config-wizard', false);
export const enableContextAttributesAutocompleteGuards = createFlagFunction(
  'enable-context-attributes-autocomplete-guards',
  false,
);
export const enableContextAttributesTypeahead = createFlagFunction('context-attributes-typeahead', false);
export const enableContextFuzzySearch = createFlagFunction('enable-fuzzy-search-in-contexts-search', false);
export const enableContextListFiltersQueryBuilder = createFlagFunction(
  'enable-context-list-filters-query-builder',
  false,
);
export const enableContextNamesInTargetingRules = createFlagFunction('enable-context-names-in-targeting-rules', false);
export const enableContextPickerSearchGuards = createFlagFunction('enable-context-picker-search-guards', false);
export const enableContextPickerTargetSuggestionCap = createFlagFunction(
  'enable-context-picker-target-suggestion-cap',
  false,
);
export const enableContextualFeedbackBot = createFlagFunction('enable-contextual-feedback-bot', false);
export const enableCooloffRetrial = createFlagFunction('enable-cooloff-retrial', false);
export const enableCooloffRetrialExtension = createFlagFunction('enable-cooloff-retrial-extension', false);
export const enableCountDistinctSimpleMetrics = createFlagFunction('enable-count-distinct-simple-metrics', false);
export const enableCreatingAppsAndVersions = createFlagFunction('enable-create-apps-and-versions', false);
export const enableCsvInjectionPrevention = createFlagFunction('enable-csv-injection-prevention', false);
export const enableCurrencyFormatMoney = createFlagFunction('enable-currency-format-money', false);
export const enableCustomApprovalsWithCustomWorkflow = createFlagFunction(
  'enable-custom-approvals-with-custom-workflow',
  false,
);
export const enableCustomRolesAsSamlDefaultRole = createFlagFunction<boolean>(
  'custom-roles-as-saml-default-role',
  false,
);
export const enableDataDogRumAllowedTracingUrls = createFlagFunction(
  'enable-web-app-data-dog-rum-allowed-tracing-urls',
  false,
);
export const enableDataDogRumInteractions = createFlagFunction('enable-web-app-data-dog-rum-interactions', false);
export const enableDataDogRumResourceTracking = createFlagFunction(
  'enable-web-app-data-dog-rum-resource-tracking',
  false,
);
export const enableDataExportUsageEnhancements = createFlagFunction('enable-data-export-usage-enhancements', false);
export const enableDatabricksDataExportOauthAuth = createFlagFunction(
  'enable-databricks-data-export-oauth-auth',
  false,
);
export const enableDatabricksExperimentationOauthAuth = createFlagFunction(
  'enable-databricks-experimentation-oauth-auth',
  false,
);
export const enableDatasetEditor = createFlagFunction('enable-dataset-editor', false);
export const enableDatasetsLibrary = createFlagFunction('datasets-library-ui', false);

export const enableDatasetViewer = createFlagFunction('enable-dataset-viewer', false);

export const enableDatavizAiPoweredFeatures = createFlagFunction(
  'enable-dataviz-ai-powered-features-moonshots-xxiii',
  false,
);

export const enableUiAiSummaryMonitoringFeedback = createFlagFunction(
  'enable-ui-ai-summary-monitoring-feedback',
  false,
);

export const enableDatavizComponentInPA = createFlagFunction('enable-dataviz-component-in-pa', false);
export const enableDatavizFlagEvalsChart = createFlagFunction('enable-dataviz-flag-evals-chart', false);
export const enableDatavizTracesTable = createFlagFunction('enable-dataviz-traces-table', false);
export const enableDateTimePickerCalendarLayout = createFlagFunction('enable-datetimepicker-calendar-layout', false);
export const enableDeveloperFacade = createFlagFunction('enable-developer-facade', true);
export const enableDeveloperPlanCheckout = createFlagFunction('enable-developer-plan-checkout', false);
export const enableDomainMatching = createFlagFunction('enable-domain-matching', false);
export const enableDomainVerificationForSSOAccounts = createFlagFunction(
  'enable-domain-verification-for-sso-accounts',
  false,
);
export const enableDuplicateSegmentRuleWithNestedSegmentClause = createFlagFunction(
  'enable-duplicate-segment-rule-with-nested-segment-clause',
  false,
);
export const enableDynamicAccessCheckForResourceActions = createFlagFunction<{
  resources: Array<{ type: string; actions: string[] }>;
}>('enable-dynamic-access-check-for-resource-actions', { resources: [] });
export const enableEmailOwnerVerificationFlow = createFlagFunction('enable-email-owner-verification-flow', false);
export const enableEMAULimitAndUsage = createFlagFunction('enable-emau-limit-and-usage', false);
export const enableEncryptedSAMLAssertions = createFlagFunction('enable-encrypted-saml-assertions', false);
export const enableEnhancedSupportControl = createFlagFunction('enable-enhanced-support-control', false);
export const enableEnterpriseOptInToAgentControl = createFlagFunction(
  'enable-enterprise-opt-in-to-agent-control',
  false,
);
export const enableEnterpriseSeatExpansions = createFlagFunction('enable-enterprise-seat-expansions', false);
export const enableEnvironmentReleasePlans = createFlagFunction('enable-environment-release-plans', false);
export const enableEnvironmentReleasesForTargeting = createFlagFunction(
  'enable-environment-releases-for-targeting',
  false,
);
export const enableEnvironmentSelectorOnPACharts = createFlagFunction(
  'enable-environment-selector-on-pa-charts',
  false,
);
export const enableEnvironmentSwitcherUrlProjectContext = createFlagFunction(
  'environment-switcher-url-project-context',
  false,
);
export const enableErrorStateOnExperimentFilters = createFlagFunction(
  'enable-error-state-on-pa-experiment-filters',
  false,
);
export const enableEventExplorerBatchFetching = createFlagFunction('enable-event-explorer-batch-fetching', false);
export const enableExperimentationAnalyticsTab = createFlagFunction('experimentation-analytics-tab', false);
export const enableExperimentationKeysLimit = createFlagFunction('enable-experimentation-keys-limit', false);
export const enableExperimentationProductAnalyticsBridge = createFlagFunction('exp-pa-bridge-enabled', false);
export const enableExperimentBypassApprovals = createFlagFunction('enable-experiment-bypass-approvals', false);
export const enableExperimentStopApprovalReasonPrefill = createFlagFunction(
  'enable-experiment-stop-approval-reason-prefill',
  false,
);
export const enableExperimentDashboardSearch = createFlagFunction('experiments-dashboard-search', false);
export const enableExperimentDashboardStatusFilter = createFlagFunction('experiment-dashboard-status-filter', false);
export const enableExperimentMaintainerUpdates = createFlagFunction('enable-maintainer-updates', false);
export const enableExperimentNotificationsFrontend = createFlagFunction(
  'enable-experiment-notifications-frontend',
  false,
);
export const enableExperimentResultsLayoutPolish = createFlagFunction('enable-experiment-results-layout-polish', false);
export const enableExperimentResultsSciNotation = createFlagFunction('enable-experiment-results-sci-notation', false);
export const enableExperimentRuleSaveFix = createFlagFunction('enable-experiment-rule-save-fix', false);
export const enableExperimentSidebarImprovements = createFlagFunction('enable-experiment-sidebar-improvements', false);
export const enableExpExposuresList = createFlagFunction('enable-exp-exposures-list', false);
export const enableExtendedMonitoringEvalHistory = createFlagFunction('enable-extended-monitoring-eval-history', false);
export const enableFeatureMonitoringChartHyperlinks = createFlagFunction('feature-monitoring-chart-hyperlinks', false);
export const enableFetchProfileContextFromAPI = createFlagFunction('enable-fetch-profile-context-from-api', false);
export const enableFilterFlagsByCreationDate = createFlagFunction(
  'enable-filter-flags-by-creation-date-via-dashboard',
  false,
);
export const enableFilterServiceTokenEntriesInChangeHistory = createFlagFunction(
  'enable-filter-service-token-entries-in-change-history',
  false,
);
export const enableFlagChangePreviews = createFlagFunction('enable-flag-change-previews', false);
export const enableFlagCleanupStatesFilter = createFlagFunction('enable-flag-cleanup-states-filter', false);
export const enableFlagCreationFeaturePreview = createFlagFunction('enable-simplify-create-feature-preview', false);
export const enableFlagEntitySidebarImprovements = createFlagFunction('flag-entity-sidebar-improvements', false);
export const enableFlagEvaluationsCheckInExperimentSetupChecklist = createFlagFunction(
  'enable-flag-evaluations-check-in-experiment-setup-checklist',
  false,
);
export const enableFlagFiltersAppViewNegations = createFlagFunction('enable-flag-filters-app-view-negations', false);
export const enableFlagJsonDiffing = createFlagFunction('enable-flag-json-diffing', false);
export const enableFlagListColumnHeaderActions = createFlagFunction('enable-flag-list-column-header-actions', false);
export const enableFlagLifecycleSettings = createFlagFunction('enable-flag-lifecycle-settings', true);
export const enableFlagListBulkActions = createFlagFunction('enable-flag-list-bulk-actions', false);
export const enableFlagListNext = createFlagFunction('enable-flag-list-next', false);
export const enableFlagListNextFeaturePreview = createFlagFunction('enable-flag-list-next-feature-preview', false);
export const enableFlagOverview = createFlagFunction('enable-flag-overview', false);
export const enableFlagReleaseDashboard = createFlagFunction('enable-flag-release-dashboard', false);
export const enableFlagSaveReceipt = createFlagFunction('enable-flag-save-receipt', false);
export const enableFlagVegaConversations = createFlagFunction('enable-flag-vega-conversations', false);
export const enableFlattenedSettings = createFlagFunction('flattened-settings', false);
export const enableFloatingHelpButton = createFlagFunction('enable-floating-help-button', false);
export const enableFoundationCostCalculator = createFlagFunction('enable-foundation-cost-calculator', false);
export const enableFullPageUiForDatabricksWarehouseNativeIntegration = createFlagFunction(
  'enable-full-page-ui-for-databricks-warehouse-native-integration',
  false,
);
export const enableFullPageUiForRedshiftNativeExperimentation = createFlagFunction(
  'enable-full-page-ui-for-redshift-native-experimentation',
  false,
);
export const enableFullStoryLinksInDataDog = createFlagFunction('enable-fullstory-links-in-data-dog', false);
export const enableGDPRComplianceCheckboxes = createFlagFunction('gdpr-compliance-checkboxes', false);
export const enableGenAIFlagTemplates = createFlagFunction('enable-gen-ai-flag-templates', false);
export const enableGenericFollowersApi = createFlagFunction('enable-generic-followers-api', false);
export const enableGetContextKindEndpoint = createFlagFunction('enable-get-context-kind-endpoint', false);
export const enableGetEvalCounts = createFlagFunction('EnableGetEvalCounts', false);
export const enableGetSegmentsBatching = createFlagFunction('enable-get-segments-batching', false);
export const enableGuardedRolloutVegaInvestigation = createFlagFunction(
  'enable-guarded-rollout-vega-investigation',
  false,
);
export const enableHideSidebarItemsUntilSdk = createFlagFunction('enable-hide-sidebar-items-until-sdk', false);
export const enableHideToggleOnConfigTargeting = createFlagFunction('enable-hide-toggle-on-config-targeting', false);
export const enableIdentityLens = createFlagFunction('enable-identity-lens', false);
export const enableImeSafeSelectInput = createFlagFunction('enable-ime-safe-select-input', false);
export const enableImmediateReleaseMethod = createFlagFunction('enable-immediate-release-method', false);
export const enableImprovedRuleCreation = createFlagFunction('improved-targeting-rule-creation', false);
export const enableImprovementsOnIntegrationCardAndListItems = createFlagFunction(
  'new-integrations-card-and-list',
  false,
);
export const enableInstallAiSdkNav = createFlagFunction('enable-install-ai-sdk-nav', false);
export const enableFilteringOnEventExplorerPage = createFlagFunction('enable-filtering-on-event-explorer-page', false);
export const enableFilteringOnIntegrationsPage = createFlagFunction('enable-filtering-on-integrations-page', false);
export const enableIntegrationsPageImmutableReduxMigration = createFlagFunction(
  'integrations-page-immutable-redux-migration',
  false,
);
export const enableIntercomChatAI = createFlagFunction('enable-intercom-chat-ai', false);
export const enableInviteMembersFlowUIRedesign = createFlagFunction('invite-members-flow-ui-redesign', false);
export const enableIpAllowlist = createFlagFunction('enable-ip-allowlist', true);
export const enableIpAllowlistSearchPagination = createFlagFunction('enable-ip-allowlist-search-pagination', false);
export const enableIterativeDiffRendering = createFlagFunction('enable-iterative-diff-rendering', false);
export const enableJsonSchemaForFlagVariations = createFlagFunction('enable-json-schema-for-flag-variations', true);
export const enableJudgeCopilot = createFlagFunction('enable-judge-copilot', false);
export const enableJudgeModelPicker = createFlagFunction('enable-judge-model-picker', false);
export const enableJudgesInUse = createFlagFunction('enable-judges-in-use', false);
export const enableLanguagePicker = createFlagFunction('enable-language-picker', false);
export const enableLanguagePickerFeaturePreview = createFlagFunction('enable-language-picker-feature-preview', false);
export const enableLaunchLitely = createFlagFunction('enable-launchlitely', false);
export const enableLaunchpadColorsOnPACharts = createFlagFunction('enable-launchpad-colors-for-pa-charts', false);
export const enableLaunchpadComponentsPACohortDefinition = createFlagFunction(
  'enable-launchpad-components-pa-cohort-definition',
  false,
);
export const enableLDidVerificationForSalesforce = createFlagFunction(
  'ld-account-id-verification-for-salesforce',
  false,
);
export const enableLoginRememberEmail = createFlagFunction('enable-login-remember-email', false);
export const enableLogoutUrlForSamlConfigs = createFlagFunction('enable-logout-url-for-saml-configs', false);
export const enableLoosenFlagNameDescriptionConfirmationRequirements = createFlagFunction(
  'enable-loosen-flag-name-description-confirmation-comment-requirements',
  false,
);
export const enableManualReleaseTriggers = createFlagFunction('enable-manual-release-triggers', false);
export const enableManuallyTrackedDdrumViews = createFlagFunction('enable-manually-tracked-ddrum-views', false);
export const enableMemberListEmailDedupe = createFlagFunction('enable-member-list-email-dedupe', false);
export const enableMergeDashboardAndCohortFilters = createFlagFunction(
  'merge-dashboard-and-chart-cohort-filters',
  false,
);
export const enableMetricFiltersOnPA = createFlagFunction('metric-filters-on-pa', false);
export const enableMetricListFiltersQueryBuilder = createFlagFunction(
  'enable-metric-list-filters-query-builder',
  false,
);
export const enableMetricSelectorAutogeneratedCategories = createFlagFunction(
  'enable-metric-selector-autogenerated-categories',
  false,
);
export const enableMetricVersionRefresh = createFlagFunction('enable-metric-version-refresh', false);
export const enableMigrationFlags = createFlagFunction('use-case-2-migrations-main-control', true);
export const enableModalConfirmationAutofocus = createFlagFunction('enable-modal-confirmation-autofocus', false);
export const enableModelParamsAsJson = createFlagFunction('enable-model-params-as-json', false);
export const enableMultiArmBanditsExtraDashboardColumns = createFlagFunction(
  'enable-multi-armed-bandits-extra-dashboard-columns',
  false,
);
export const enableNewAccessTokenExpiryField = createFlagFunction('enable-new-access-token-expiry-field', false);
export const enableNewAiConfigListView = createFlagFunction('enable-new-ai-config-list-view', false);
export const enableNewAiSdkOnboarding = createFlagFunction('enable-new-ai-sdk-onboarding', false);
export const enableNewChartTooltip = createFlagFunction('new-chart-tooltip', false);
export const enableNewFlagStatusesApi = createFlagFunction('enable-new-flag-statuses-api', false);
export const enableNewFormulaUXOnTrendsChart = createFlagFunction('enable-new-formula-ux-on-trends-chart', false);
export const enableNewHomePage = createFlagFunction<'off' | 'with_sim' | 'without_sim'>(
  'enable-new-home-page-experiment',
  'off',
);
export const enableNewInviteMembersPermissionsLogic = createFlagFunction(
  'enable-new-invite-members-permissions-logic',
  false,
);
export const enableNewNavigation = createFlagFunction('new-navigation', false);
export const enableNewProductAnalyticsEditableText = createFlagFunction('enable-new-pa-editable-text', false);
export const enableNewSessionColumnRenderer = createFlagFunction('new-session-column-renderer', false);
export const enableNewTeamEvaluationWelcomeSurveyField = createFlagFunction(
  'enable-new-team-evaluation-welcome-survey-field',
  false,
);
export const enableNewUISnowflakeDataExport = createFlagFunction('enable-new-ui-snowflake-data-export', false);
export const enableNoMaintainerFlagFilter = createFlagFunction('enable-no-maintainer-flag-filter', false);
export const enableNotificationSettingsChatColumnVariations = createFlagFunction(
  'enable-notification-settings-chat-column-variations',
  true,
);
export const enableO11yJiraAlertDestination = createFlagFunction('o11y-jira-alert-destination', false);
export const enableO11yJiraIssueCreate = createFlagFunction('o11y-jira-issue-create', false);
export const enableObservabilityAiAutoInvestigations = createFlagFunction(
  'enable-observability-ai-auto-investigations',
  false,
);
export const enableObservabilityAiFeatures = createFlagFunction('enable-observability-ai-features', false);
export const enableObservabilityAiGraphCreation = createFlagFunction('enable-observability-ai-graph-creation', false);
export const enableObservabilityAiSuggestedPrompts = createFlagFunction(
  'enable-observability-ai-suggested-prompts',
  true,
);
export const enableObservabilityAiTestError = createFlagFunction('enable-observability-ai-test-error', false);
export const enableObservabilityAlertTriggerUpdates = createFlagFunction(
  'enable-observability-alert-trigger-updates',
  false,
);
export const enableObservabilityCanvasArgAllowlist = createFlagFunction(
  'enable-observability-canvas-arg-allowlist',
  false,
);
export const enableObservabilityConcurrentBrowserTabSessions = createFlagFunction(
  'enable-observability-concurrent-browser-tab-sessions',
  false,
);
export const enableObservabilityConversationView = createFlagFunction('enable-observability-conversation-view', false);
export const enableObservabilityConversationsTab = createFlagFunction('enable-observability-conversations-tab', false);
export const enableObservabilityDatabases = createFlagFunction('enable-observability-databases', false);
export const enableEvaluationRunConversation = createFlagFunction('enable-evaluation-run-conversation', false);
export const enableObservabilityEarlyAccessBadge = createFlagFunction('enable-observability-early-access-badge', true);

export const enableObservabilityErrorsSettingsSelectSizing = createFlagFunction(
  'observability-errors-settings-select-sizing',
  false,
);

export const enableObservabilityExplorerFlagOverlays = createFlagFunction(
  'enable-observability-explorer-flag-overlays',
  false,
);

export const enableObservabilityLogsAdvancedSearch = createFlagFunction(
  'enable-observability-logs-advanced-search',
  false,
);

export const enableObservabilityMetricExemplars = createFlagFunction('enable-observability-metric-exemplars', false);
export const enableObservabilityMetricsAdvancedSearch = createFlagFunction(
  'enable-observability-metrics-advanced-search',
  false,
);

export const enableObservabilityPaEvents = createFlagFunction('enable-observability-pa-events', false);
export const enableObservabilityQueryBuilder = createFlagFunction('enable-observability-query-builder', false);
export const enableObservabilityRBAC = createFlagFunction('observability-rbac', false);
export const enableObservabilitySearchLaunchpadControls = createFlagFunction(
  'enable-observability-search-launchpad-controls',
  false,
);
export const enableObservabilitySessionExport = createFlagFunction('enable-observability-session-export', false);
export const enableObservabilitySessionDevtoolsTabPerformance = createFlagFunction(
  'enable-observability-session-devtools-tab-performance',
  false,
);
export const observabilitySessionReplayIsolatedHostOrigin = createFlagFunction(
  'observability-session-replay-isolated-host-origin',
  'https://prod.launchdarklysessions.com',
);
export const enableObservabilitySessionReplayPlayOverlay = createFlagFunction(
  'enable-observability-session-replay-play-overlay',
  false,
);
export const enableObservabilitySessionReplayEventFilter = createFlagFunction(
  'enable-observability-session-replay-event-filter',
  false,
);

export const enableObservabilityDatatable = createFlagFunction('enable-observability-datatable', false);

export const enableOpencodeObservabilitySearch = createFlagFunction('enable-opencode-observability-search', false);

export const enableObservabilitySessionsTableView = createFlagFunction(
  'enable-observability-sessions-table-view',
  false,
);

export const enableObservabilityErrorsTableView = createFlagFunction('enable-observability-errors-table-view', false);

export const enableObservabilitySessionEventQuerying = createFlagFunction(
  'enable-observability-session-event-querying',
  false,
);

export const enableObservabilitySessionSpanSearch = createFlagFunction(
  'enable-observability-session-span-search',
  true,
);

// Trace waterfall names and span-path breadcrumbs: rich hover card instead of native title=.
export const enableObservabilitySpanHoverCard = createFlagFunction('enable-observability-span-hover-card', false);

export const enableObservabilitySqlBuilder = createFlagFunction('o11y-sql-builder-enabled', false);
export const enableObservabilityTeamQueryHistory = createFlagFunction('enable-observability-team-query-history', false);

// Traces span-details drawer: always-present Configs/Flags/Errors tabs with counts, and
// Flags/AI-config observations aggregated across the whole trace instead of the selected span.
export const enableObservabilityTraceScopedSpanTabs = createFlagFunction(
  'enable-observability-trace-scoped-span-tabs',
  false,
);

export const enableObservabilityTraceSpanSequenceSearch = createFlagFunction(
  'enable-observability-trace-span-sequence-search',
  false,
);

export const enableOneTimeAnnouncementSystem = createFlagFunction('enable-one-time-announcement-system', false);

export const enableOpenSeatsSnackbarNotification = createFlagFunction('enable-open-seats-snackbar-notification', false);

export const enableOptimizationForPaCatFood = createFlagFunction('enable-optimzation-for-pa-catfood', false);

export const enableOptInToAiConfigs = createFlagFunction('enable-opt-in-to-ai-configs', false);

export const enableOrgSettingsQueryBuilder = createFlagFunction('enable-org-settings-query-builder', false);

export const enableSelectMaintainerTagsMigration = createFlagFunction('enable-select-maintainer-tags-migration', false);

export const enableOutOfTheBoxRoles = createFlagFunction('enable-out-of-the-box-roles-fe', false);

export const enableOutOfTheBoxRolesBE = createFlagFunction('enable-ootb-roles', false);

export const enablePaDisableEntitySelectionCategory = createFlagFunction('enable-pa-disabled-entity-category', false);

export const enablePadthaiDashboard = createFlagFunction('enable-padthai-dashboard', false);

export const enablePaginationOnGetSegments = createFlagFunction('use-pagination-for-get-segments', false);

export const enablePauseForSNEIntegration = createFlagFunction('enable-pause-for-sne-integration', false);

export const enablePauseForSnowflakeExportIntegration = createFlagFunction(
  'enable-pause-for-snowflake-export-integration',
  false,
);

export const enablePayloadFilteringV2 = createFlagFunction('payload-filtering-v2', false);

export const enablePerRuleAdaptiveTriggers = createFlagFunction('enable-per-rule-adaptive-triggers', false);

export const enablePlanPickerRedesign = createFlagFunction('enable-plan-picker-redesign', false);

export const enablePlanUsageBanner = createFlagFunction('enable-plan-usage-banner', false);

export const enablePlanUsageBannerStaleTime = createFlagFunction('enable-plan-usage-banner-stale-time', 0);

export const enablePlanUsageCharts = createFlagFunction('enable-plan-usage-charts', false);

export const enablePlaygroundApiKeySetupBanner = createFlagFunction('enable-playground-api-key-setup-banner', false);

export const enablePlaygroundTrialExperience = createFlagFunction('enable-playground-trial-experience', false);

export const enablePredictiveTargetingApi = createFlagFunction('enable-predictive-targeting-api', false);

export const enablePredictiveTargetingFeaturePreview = createFlagFunction(
  'enable-predictive-targeting-feature-preview',
  false,
);

export const enablePredictiveTargetingFilterByApplicationKey = createFlagFunction(
  'enable-predictive-targeting-filter-by-application-key',
  false,
);

export const enablePredictiveTargetingFilterByApplicationKeyAndFlag = createFlagFunction(
  'enable-predictive-targeting-applications-by-flag',
  false,
);

export const enablePredictiveTargetingFilterByContextKinds = createFlagFunction(
  'enable-predictive-targeting-filter-by-context-kinds',
  false,
);

export const enablePricingConcierge = createFlagFunction('enable-pricing-concierge', false);
export const enablePricingConciergeByLocation = createFlagFunction<'' | 'planning-usage'>(
  'enable-pricing-concierge-by-location',
  '',
);

export const enablePrimaryContextsInUiOverride = createFlagFunction('enable-primary-contexts-in-ui-override', false);

export const enableProductAnalyticsChartSaveToNewDashboard = createFlagFunction(
  'pa-enable-create-new-dashboard-in-create-chart',
  false,
);

export const enableProductAnalyticsDashboardCustomRoles = createFlagFunction(
  'product-analytics-dashboard-support-on-custom-role-builder',
  false,
);

export const enableProjectViewSwitcher = createFlagFunction('enable-project-view-switcher', false);

export const enablePromoBanner = createFlagFunction<
  'none' | 'galaxy-2025' | 'webinar-june-2026' | 'agentcontrol-2026' | 'factory-beta-2026' | 'api-deprecation-2026'
>('enable-promo-banner', 'none');

export const enablePromptSnippetRbacActions = createFlagFunction('enable-prompt-snippet-rbac-actions', false);

export const enableRagBasedAcceptanceCriteria = createFlagFunction('enable-rag-based-acceptance-criteria', false);

export const enableRealTimeChartTitleAndLabels = createFlagFunction('real-time-chart-title-labels', false);

export const enableRedshiftDataExportCustomS3Bucket = createFlagFunction(
  'enable-redshift-data-export-custom-s3-bucket',
  false,
);

export const enableRefreshFlagMonitoringEvals = createFlagFunction('enable-refresh-flag-monitoring-evals', false);

export const enableRegressionFeedback = createFlagFunction('enable-regression-feedback', false);

export const enableRelaxedBillingUIAccess = createFlagFunction('enable-relaxed-billing-ui-access', false);

export const enableReleaseAutomation = createFlagFunction('release-automation', false);

export const enableReleaseAutopilotReviewPage = createFlagFunction('enable-release-autopilot-review-page', false);

export const enableReleaseCoordinatorDashboard = createFlagFunction('enable-release-coordinator-dashboard', false);

export const enableReleaseCoordinatorDashboardFeaturePreview = createFlagFunction(
  'enable-release-coordinator-dashboard-feature-preview',
  false,
);

export const enableReleasePipelinePhaseStatus = createFlagFunction('enable-release-pipeline-phase-status', false);

export const enableReleasePoliciesDeprecationWarning = createFlagFunction(
  'enable-release-policies-deprecation-warning',
  false,
);

export const enableReleasePolicyApprovalBanner = createFlagFunction('enable-release-policy-approval-banner', false);
export const enableReleasesListPage = createFlagFunction('enable-releases-list-page', false);
export const enableRemoveOrganizationFromSignup = createFlagFunction('remove-organization-from-signup-form', false);

export const enableRemovePaDashboardRefetches = createFlagFunction('remove-pa-dashboard-refetches', false);

export const enableResetToken = createFlagFunction('enable-reset-token', false);

export const enableRetentionHeatmapReadability = createFlagFunction('retention-heatmap-readability', false);

export const enableReviewerSelectionRetention = createFlagFunction('enable-reviewer-selection-retention', false);

export const enableRichViewCollapse = createFlagFunction('enable-rich-view-collapse', false);

export const enableRoleAttributesInTags = createFlagFunction('enable-role-attributes-in-tags', false);

export const enableS3DataExport = createFlagFunction('enable-s3-data-export', false);

export const enableSampleAppOfferAllTrialists = createFlagFunction('enable-sample-app-offer-all-trialists', false);

export const enableSamplingSignalsUi = createFlagFunction('enable-sampling-signals-ui', false);

export const enableScimForAzure = createFlagFunction('enable-scim-for-azure', false);

export const enableScimForRippling = createFlagFunction('enable-scim-for-rippling', false);

export const enableSdkAvailabilityMisconfigurationWarning = createFlagFunction(
  'enable-sdk-availability-misconfiguration-banner',
  false,
);

export const enableSdkKeyClarification = createFlagFunction('enable-sdk-key-clarification', false);

export const enableSdkKeysPagination = createFlagFunction('enable-sdk-keys-pagination', false);

export const enableSdkNotifications = createFlagFunction('enable-sdk-notifications', false);

export const enableSdkOnboardingClarification = createFlagFunction('enable-sdk-onboarding-clarification', false);

export const enableSdkVersionUsageDetails = createFlagFunction('enable-sdk-version-usage-details', false);

export const enableSegmentBypassApprovals = createFlagFunction('enable-segment-bypass-approvals', false);

export const enableSegmentDescriptionInlineEdit = createFlagFunction('enable-segment-description-inline-edit', false);

export const enableSegmentListFiltersQueryBuilder = createFlagFunction(
  'enable-segment-list-filters-query-builder',
  false,
);

export const enableSegmentsListTableHeaders = createFlagFunction('enable-segments-list-table-headers', false);

export const enableSegmentsOfInterest = createFlagFunction('segments-of-interest-moonshot', false);

export const enableSelectEnvironmentForInstallSDK = createFlagFunction(
  'enable-select-environment-for-install-sdk',
  false,
);

export const enableSelfServeDeveloper2025PlanCheckout = createFlagFunction(
  'enable-self-serve-developer-2025-plan-checkout',
  false,
);

export const enableSelfServeExperimentationKeysLimit = createFlagFunction(
  'enable-self-serve-experimentation-keys-limit',
  false,
);

export const enableSelfServeFoundation2025PlanCheckout = createFlagFunction(
  'enable-self-serve-foundation-2025-plan-checkout',
  false,
);

export const enableSelfServeObservability = createFlagFunction('enable-self-serve-observability', false);

export const enableSpecialRatesPlanFeature = createFlagFunction('enable-special-rates-plan-feature', false);

export const enableSelfServeOrbSubscriptions = createFlagFunction('enable-self-serve-orb-subscriptions', false);

export const enableSelfServePaygFreeUnits = createFlagFunction('enable-self-serve-payg-free-units', false);

export const selfServeTrialExtensionDays = createFlagFunction('self-serve-trial-extension-days', 14);

export const enableSelfServeUBBWithAnnualCommits = createFlagFunction(
  'enable-self-serve-ubb-commit-overage-plans',
  false,
);

export const enableSelfServeUsageBasedBilling = createFlagFunction('enable-self-serve-usage-based-billing', false);

export const enableSelfServiceEnterpriseTrials = createFlagFunction('enable-self-service-enterprise-trials', false);

export const enableSemverOperatorsForMetricFilters = createFlagFunction(
  'enable-semver-operators-for-metric-filters',
  false,
);

export const enableServiceConnectionsLimitAndUsage = createFlagFunction('enable-sc-limit-and-usage', false);

export const enableUsageProjectionUi = createFlagFunction('enable-usage-projection-ui', false);

export const enableServiceConnectionsUsageSdkAppIdBreakdown = createFlagFunction(
  'enable-service-connections-usage-sdk-app-id-breakdown',
  false,
);

export const enableAIConfigsServiceDetail = createFlagFunction('enable-ai-configs-on-service-detail-flyin', false);

export const enableServiceNowExperimentApprovals = createFlagFunction('enable-service-now-experiment-approvals', false);

export const enableServiceNowSegmentApprovals = createFlagFunction('enable-service-now-segment-approvals', false);

export const enableSettingsMenuPopover = createFlagFunction('enable-settings-menu-popover', false);

export const enableShortcutCounters = createFlagFunction('enable-shortcut-counters', false);

export const enableSidebarUpdateHoldouts = createFlagFunction('enable-sidebar-update-holdouts', false);

export const enableSignedSAMLAuthnRequests = createFlagFunction('enable-signed-saml-authentication-requests', false);

export const enableSignupOAuthReturn = createFlagFunction('enable-signup-oauth-return', false);
export const enableSignupRefererForwarding = createFlagFunction('enable-signup-referer-forwarding', false);

export const enableSinglePaChartBuilderExperience = createFlagFunction(
  'enable-single-pa-chart-builder-experience',
  false,
);

export const enableSmootherPAChartsTooltip = createFlagFunction('enable-smoother-pa-charts-tooltip', false);

export const enableBigQueryExperimentationWifAuth = createFlagFunction(
  'enable-bigquery-experimentation-wif-auth',
  false,
);

export const enableBigQueryExperimentationDataExportPrefill = createFlagFunction(
  'enable-bigquery-experimentation-data-export-prefill',
  false,
);

export const enableBqDataExportWifAuth = createFlagFunction('enable-bq-data-export-wif-auth', false);

export const enableSnippetsInPlaygrounds = createFlagFunction('enable-snippets-in-playgrounds', false);

export const enableSnowflakeNativeFrontend = createFlagFunction('snowflake-exp-frontend', false);

export const enableSnowflakeWneCustomUserName = createFlagFunction('enable-snowflake-wne-custom-user-name', false);

export const enableSnowflakeWneSeparateLoginName = createFlagFunction(
  'enable-snowflake-wne-separate-login-name',
  false,
);

export const enablePrepopulatedDefaultsWarehouseIntegrations = createFlagFunction(
  'enable-prepopulated-defaults-warehouse-integrations',
  false,
);

export const enableStickyProjectNavigationMode = createFlagFunction('enable-sticky-project-navigation-mode', false);

export const enableStringKeyClauseValues = createFlagFunction('enable-string-key-clause-values', false);

export const enableStripeUnpaidInvoicesUi = createFlagFunction('enable-stripe-unpaid-invoices-ui', false);

export const enableStructuralJsonDiffing = createFlagFunction('enable-structural-json-diffing', false);

export const enableSuggestedInvitesNotifications = createFlagFunction('enable-suggested-invites-notifications', false);

export const enableSuggestInvites = createFlagFunction('enable-suggest-invites', false);

export const enableSupportedWarehouseExperimentationManifests = createFlagFunction<string[]>(
  'supported-warehouse-experimentation-manifests',
  [],
);

export const enableSurfaceFlagTemplates = createFlagFunction('enable-surface-flag-templates', false);

export const enableSyncFlagColors = createFlagFunction('sync-flag-variation-colors', false);

export const enableTeamMaintainerRequirement = createFlagFunction('enable-team-maintainer-requirement', false);

export const enforceTeamMaintainerOnFlagClone = createFlagFunction('enforce-team-maintainer-on-flag-clone', false);

export const enableViewsOnFlagClone = createFlagFunction('enable-views-on-flag-clone', false);

export const enableTeamsAsFlagMaintainers = createFlagFunction('enable-teams-as-flag-maintainers', true);

export const enableTeamsCsvUpload = createFlagFunction('enable-teams-csv-upload', false);

export const enableTeamsOfTeams = createFlagFunction('enable-teams-of-teams', true);

export const enableThemeToggleHotkey = createFlagFunction('enable-theme-toggle-hotkey', false);

export const enableToolCallsInOfflineEvaluations = createFlagFunction(
  'enable-tool-calls-in-offline-evaluations',
  false,
);

export const enableEvalsFromCode = createFlagFunction('enable-evals-from-code', false);

export const enableToolListOwnerColumn = createFlagFunction('enable-tool-list-owner-column', false);

export const enableTraceBasedMetrics = createFlagFunction('enable-trace-based-metrics', false);

export const enableTraceBasedMetricsForExp = createFlagFunction('enable-trace-based-metrics-for-exp', false);

export const enableTraceBasedMetricsForRg = createFlagFunction('enable-trace-based-metrics-for-rg', false);

export const enableTraceToDatasetPromotion = createFlagFunction('enable-trace-to-dataset-promotion', false);

export const enableTryItLiveOnboardingGuidance = createFlagFunction('enable-try-it-live-onboarding-guidance', false);

export const tryItLiveSkipWelcomeModalExperiment = createFlagFunction(
  'try-it-live-skip-welcome-modal-experiment',
  false,
);

export const enableTypescriptFunnels = createFlagFunction('enable-typescript-funnels', false);

export const enableUiFlagAudienceCsvExport = createFlagFunction('enable-ui-flag-audience-csv-export', false);

export const enableUiFlagAudienceDatavizMigration = createFlagFunction(
  'enable-ui-flag-audience-dataviz-migration',
  false,
);

export const enableUnauthenticatedTargeting = createFlagFunction('enable-unauthenticated-targeting', false);

export const enableUnauthenticatedTargetingUI = createFlagFunction('enable-unauthenticated-targeting-ui', false);

export const enableUngatedSandbox = createFlagFunction('enable-ungated-sandbox', false);

export const enableUnixTimestampsOnPaCharts = createFlagFunction('enable-unix-timestamps-on-pa-charts', false);

export const enableUnlimitedSegmentsInTargetingDropdown = createFlagFunction(
  'remove-segment-limit-for-targeting-dropdown',
  false,
);

export const enableUpdatedPATextContent = createFlagFunction('enable-updated-pa-text-content', false);

export const enableUpdatedSwitchOrganizationCheck = createFlagFunction('updated-switch-org-check', false);

export const enableUxInsights = createFlagFunction('ux-insights', false);

export const enableUsageExceededSelectiveReadOnly = createFlagFunction(
  'enable-usage-exceeded-selective-read-only',
  false,
);

export const enableVadimTestMode = createFlagFunction('enable-vadim-test-mode', false);

// Gates the Vega "automated rollout" mode in the prompt composer: the mode
// entry point and the environment selector that lets a user pick which
// environments Vega should automatically release the new flag in. Separate from
// the backend `automated-rollout-scheduler` kill-switch that gates the gonfalon
// cron job.
export const enableVegaAutomatedRollout = createFlagFunction('enable-vega-automated-rollout', false);

// Keeps the Vega prompt composer's mentions text input editable while the agent
// is generating a response, so a user can draft a follow-up while waiting.
// Submitting stays blocked until generation ends either way. Off restores the
// previous behavior, where the input is locked for the duration of the response.
export const enableVegaDraftWhileAnalyzing = createFlagFunction('enable-vega-draft-while-analyzing', false);

// Gates automatic page, filter, time-range, and resource context in Vega
// prompts. Off preserves the previous prompt behavior.
export const enableVegaPageContext = createFlagFunction('enable-vega-page-context', false);

export const enableVegaProjectSettings = createFlagFunction('enable-vega-project-settings', false);

export const enableVegaPullRequestBotIdentitySettings = createFlagFunction(
  'enable-vega-pull-request-bot-identity-settings',
  false,
);

// Gates everything that lets a user *create* or *edit* an Analytical Report
// (Brief dashboard) — the manual-create dropdown entry, the Edit-JSON button
// in the dashboard filter bar, and the brief-aware Vega request type +
// brief-content payload. Existing Brief dashboards remain *viewable* when
// off. Server-side, the same flag is evaluated in Go and forwarded to the
// autofix Python service so Vega's tool surface drops every brief MCP tool
// (reads + writes) and the BriefAuthoring request type degrades to a no-op.
export const enableVegaReportCreation = createFlagFunction('enable-vega-report-creation', false);

export const enableVegaWithoutGenAiTerms = createFlagFunction('enable-vega-without-genai-terms', false);

export const enableViewCreationFromSelect = createFlagFunction('enable-view-create-from-select', false);

export const enableViewFavoriting = createFlagFunction('enable-view-favoriting', true);

export const enableViewRequirementProjectSetting = createFlagFunction('enable-view-requirement-project-setting', true);

export const enableViewSettingsTabRedesign = createFlagFunction('enable-view-settings-tab-redesign', false);

export const enableViews = createFlagFunction('enable-views', false);

export const enableViewsListAndHeaderRedesign = createFlagFunction('enable-views-list-and-header-redesign', false);

export const environmentCriticalFilter = createFlagFunction('environment-critical-filter', false);

export const enforceEmailVerificationLandingPage = createFlagFunction('enforce-email-verification-landing-page', false);

export const enforceResourceNameLength = createFlagFunction('enforce-resource-name-length', true);

export const evalsTokenLimitsMaxPerMember = createFlagFunction('evals-token-limits-max-per-member', 10_000_000);

export const expandableErrorsListVisible = createFlagFunction('expandable-errors-list-visible', false);

export const experimentAllocationForceOver = createFlagFunction<'warn' | 'block' | 'off'>(
  'experiment-allocation-force-over',
  'off',
);

export const experimentationAlertBanner = createFlagFunction<{
  enabled: boolean;
  message: string;
  link: string;
  linkText: string;
  title: string;
  status: 'warning' | 'info' | 'error';
  isDismissable?: boolean;
  dismissalKey?: string;
  dismissDurationDays?: number;
  pageOverrides?: Record<
    string,
    {
      enabled?: boolean;
      message?: string;
      link?: string;
      linkText?: string;
      title?: string;
    }
  >;
}>('experimentation-alert-banner', {
  enabled: false,
  message: '',
  link: '',
  linkText: '',
  title: '',
  status: 'warning',
  pageOverrides: {},
});

export const experimentationGetStarted = createFlagFunction<'' | 'sdk-install' | 'experimentation-empty-state'>(
  'experimentation-get-started',
  '',
);

export const experimentAttributeLimit = createFlagFunction('experiment-attribute-limit', 20);

export const experimentChartHeight = createFlagFunction('experiment-results-chart-height', 472);

export const experimentHypothesisAssistSettings = createFlagFunction<{
  api: 'agent' | 'fast';
  showDiagnostics: boolean;
  showApiSwitch: boolean;
  // When true, the AI assist toggle starts on so the panel shows without opting in.
  defaultOn?: boolean;
}>('experiment-hypothesis-assist-settings', {
  api: 'fast',
  showDiagnostics: false,
  showApiSwitch: false,
  defaultOn: true,
});

// On/off gate for the whole AI-assisted hypothesis feature (the Vega assist entry point).
export const aiAssistedExpHypothesisGuidance = createFlagFunction('ai-assisted-exp-hypothesis-guidance', false);

export const experimentMetricActiveMocking = createFlagFunction<'disabled' | 'enabled' | 'true' | 'false'>(
  'experiment-metric-active-mocking',
  'disabled',
);

export const experimentPaginationLimit = createFlagFunction('experiment-dashboard-page-size', 30);

export const experimentResultsUseTrafficField = createFlagFunction('experiment-results-use-traffic-field', false);

export const experimentSampleSizeCalculator = createFlagFunction('experiment-sample-size-calculator', false);

export const experimentsDefaultDisableReshuffling = createFlagFunction(
  'experiments-default-disable-reshuffling',
  false,
);

export const experimentsFixedHorizonDefault = createFlagFunction('experiments-fixed-horizon-default', false);
export const experimentsNewPercentileEnforcement = createFlagFunction('experiments-new-percentile-enforcement', false);

export const experimentValueThreshold = createFlagFunction('experiment-results-value-render-threshold', 0.01);

export const exposuresCsvExportMaxLimit = createFlagFunction('exposures-csv-export-max-limit', 1000);

export const fallbackDisplayOptions = createFlagFunction<'default' | 'hidden' | 'v1' | 'v1-with-value' | 'v2' | 'v3'>(
  'fallback-display-options',
  'default',
);

export const featureMonitoringContextCountsChart = createFlagFunction('enable-fm-context-counts-chart', false);

export const featureMonitoringVersion = createFlagFunction('feature-monitoring-version-frontend', 'disabled');

export const featurePreview = createFlagFunction('feature-preview', false);

export const fetchCleanupShortcutCountViaInsightsEndpoint = createFlagFunction(
  'fetch-cleanup-shortcut-count-via-insights-endpoint',
  false,
);

export const filterTabEnterArrowsFromAutocompleteExperimental = createFlagFunction(
  'filter-tab-enter-arrows-from-autocomplete-experimental',
  false,
);

export const fixAiConfigMissingEnvironmentFallback = createFlagFunction(
  'fix-ai-config-missing-environment-fallback',
  false,
);

export const fixPendingChanges429 = createFlagFunction('fix-pending-changes-429', false);

export const flagArchivalMessage = createFlagFunction(
  'flag-archival-message',
  'After you archive this flag, LaunchDarkly will serve the default value defined in your code',
);

export const flagActionMenuLifecycleGroup = createFlagFunction('flag-action-menu-lifecycle-group', false);

export const flagCleanupGitarPollingInterval = createFlagFunction('flag-cleanup-gitar-polling-interval', 30);

export const flagCleanupSpikeGitar = createFlagFunction('flag-cleanup-spike-gitar', false);

export const flagCleanupVegaCta = createFlagFunction('flag-cleanup-vega-cta', false);

export const flagDependencyWarningsEnabled = createFlagFunction('enable-flag-dependency-warnings', false);

export const flagEvalsChartRawVariationValues = createFlagFunction('flag-evals-chart-raw-variation-values', false);

export const flagEvaluationUiRefreshRate = createFlagFunction('flag-evaluation-ui-refresh-rate', -1);

export const flagImportFalseDoor = createFlagFunction('flag-import-false-door', false);

export const flagLifecycleArchiveModalCriticalEnvironmentsLimit = createFlagFunction(
  'flag-lifecycle-archive-modal-critical-environments-fetch-limit',
  20,
);

export const flagListDisplayPreferences = createFlagFunction('flag-list-display-preferences', false);

export const flagListEmptyStateFallback = createFlagFunction('flag-list-empty-state-fallback', false);

export const flagListLimitGuardrail = createFlagFunction('flag-list-limit-guardrail', false);

export const flagListSummaryPopover = createFlagFunction('flag-list-summary-popover', false);

// JSON Schema dialect spec for flag variation schemas (e.g. "draft-07",
// "2020-12", "draft-07,2020-12", or "" for no enforcement). Mirrors the backend
// `FlagSchemaDialect()` flag; default matches `flag_defs.go`. (REL-13790)
export const flagSchemaDialect = createFlagFunction<string>('flag-schema-dialect', 'draft-07,2020-12');

export const flagsQueryEnvironmentKeyLimit = createFlagFunction('flags-query-environment-key-limit', 10);

export const flagsQueryFlagKeyLimit = createFlagFunction('flags-query-flag-key-limit', 100);

export const flagStatusesEnvironmentKeyLimit = createFlagFunction('flag-statuses-environment-key-limit', 10);

export const flagStatusesFlagKeyLimit = createFlagFunction('flag-statuses-flag-key-limit', 100);

export const flagTargetingViaAI = createFlagFunction('flag-targeting-via-ai', false);

export const flagVariationRACFields = createFlagFunction('flag-variation-rac-fields', false);

export const flowsChartMaxDateRangeInDays = createFlagFunction('pa-flows-chart-max-date-range', {
  unit: 'day',
  value: 14,
});

export const forceTeamMaintainerAtFlagCreation = createFlagFunction('force-team-maintainer-at-flag-creation', false);

export const foundation2023PlanVersion = createFlagFunction('foundation-2023-plan-version', 1);

export const frequentistEventSumColumns = createFlagFunction('frequentist-event-sum-columns', false);

export const frontendDatadogFlagRumTracking = createFlagFunction('frontend-datadog-flag-rum-tracking', false);

export const frontendLogLevel = createFlagFunction('frontend-log-level', 'warn');

export const frontendQueryCacheGcTime = createFlagFunction('frontend-query-cache-gc-time', 5 * 60 * 1000);

// 5 minutes (@tanstack/query default)
export const frontendQueryCachePersistence = createFlagFunction<'disabled' | 'localstorage' | 'indexeddb'>(
  'frontend-query-cache-persistence',
  'localstorage',
);

export const frontendVersionPollingIntervalMs = createFlagFunction(
  'frontend-version-polling-interval-ms',
  60 * 60 * 1000, // 1 hour
);

export const funnelGroupMetricLimit = createFlagFunction('funnel-group-metric-limit', 10);

export const getCodeReferenceListPageSize = createFlagFunction('code-reference-list-page-size', 10);

export const getExperimentListPageSize = createFlagFunction('experiment-list-page-size', 50);

export const getFlagListPageSize = createFlagFunction('flag-list-page-size', 250);

export const getMemberListPageSize = createFlagFunction('member-list-page-size', 25);

export const getNativeBigSegmentImportMaxFileSize = createFlagFunction('native-big-segment-import-max-file-size', 20);

// Curated model keys (Provider.modelId) a trial Playground user may pick from.
export const getPlaygroundTrialAvailableModels = createFlagFunction<string[]>('playground-trial-available-models', [
  'Anthropic.claude-sonnet-4-6',
  'Anthropic.claude-haiku-4-5-20251001',
]);

// Daily token cap applied to trial Playground accounts; surfaced in the trial CTA copy.
// Default mirrors the backend default (playground-trial-daily-token-limit).
export const getPlaygroundTrialDailyTokenLimit = createFlagFunction('playground-trial-daily-token-limit', 1000);

export const getProjectEnvironmentsPageSize = createFlagFunction('project-environments-page-size', 20);

export const getProjectListPageSize = createFlagFunction('project-list-page-size', 20);

export const getStratifiedSamplingMaxUploadSizeMb = createFlagFunction('stratified-sampling-max-upload-size-mb', 50);

export const githubVegaAppUrlSlug = createFlagFunction('github-vega-app-url-slug', '');

export const giveFeedback = createFlagFunction('give-feedback', false);

export const guardedRolloutSafeRollForwardThreshold = createFlagFunction('safe-roll-forward-threshold', 0);

export const guardianAlertBanner = createFlagFunction<{
  enabled: boolean;
  message: string;
  releaseKind: 'guarded' | 'progressive';
  title: string;
  status: 'warning' | 'info' | 'error';
}>('guardian-alert-banner', {
  enabled: false,
  message: '',
  releaseKind: 'guarded',
  title: '',
  status: 'warning',
});

export const hideEmptyModelProviders = createFlagFunction('hide-empty-model-providers', false);

export const hideO11yUsageBar = createFlagFunction('hide-o11y-usage-bar', false);

export const hideRolloutSummaryAfterDays = createFlagFunction('hide-rollout-summary-after-n-days', 0);

export const hideSdkKeysInSandbox = createFlagFunction('hide-sdk-keys-in-sandbox', false);

export const historicalReleasesDefaultLookbackDays = createFlagFunction(
  'historical-releases-default-lookback-days',
  90,
);

export const holdoutMaximumAmount = createFlagFunction('maximum-holdout-amount', 99);

export const improvedTimePaging = createFlagFunction('improved-time-paging', false);

export const inspectVariationsFromFlagList = createFlagFunction('inspect-variations-from-flag-list', false);

export const installSdkVisibility = createFlagFunction('install-sdk-visibility', false);

export const integrationConfigSidePanelKinds = createFlagFunction<string[]>('integration-config-side-panel-kinds', [
  'redshift',
]);

// Default matches flag_defs.go: far-future so started iterations are ineligible until the cutoff is lowered.
export const iterationStartedAtCupedChexCutoff = createFlagFunction(
  'iteration-started-at-cuped-chex-cutoff',
  2082758400000,
);

export const is2018ProTo2021PlanTransitionEnabled = createFlagFunction('enable-2018-pro-to-2021-plan-transition', true);

export const isABTestingEnabled = createFlagFunction('ab.testing', false);

export const isAccessTokensEnabled = createFlagFunction('enable-access-tokens', true);

export const isAccessWarningEnabled = createFlagFunction('enable-access-warning', false);

export const isAccountDeletionEnabled = createFlagFunction('enable-account-deletion', false);

export const isAIConfigBedrockImportEnabled = createFlagFunction('enable-ai-config-bedrock-import', false);

export const isAllowSettableFlagSaltEnabled = createFlagFunction('allow-settable-flag-salt', false);

export const isAnalyticsTrackingEnabled = createFlagFunction('enable-segment-script', true);

export const isApprovalByTeamEnabled = createFlagFunction('enable-approval-by-team', true);

export const isApprovalsEnabled = createFlagFunction('enable-approvals', true);

export const isAuditLogEnabled = createFlagFunction('enable-audit-log', false);

export const isAutoApplyApprovalsEnabled = createFlagFunction('enable-approval-auto-apply', false);

export const isAutoCleanupFactorySettingsEnabled = createFlagFunction('enable-auto-cleanup-factory-settings', false);

export const isBigSegmentMergeEnabled = createFlagFunction('enable-big-segment-merge-functionality', false);

export const isBigSegmentsEnabled = createFlagFunction('enable-unbounded-segments', false);

export const isBillingMaintenanceModeEnabled = createFlagFunction('billing-maintenance-mode', false);

export const isBulkMemberEditEnabled = createFlagFunction('enable-bulk-member-edit', false);

export const isBulkUserManagementEnabled = createFlagFunction('enable-bulk-user-management', true);

export const isCancelSubscriptionEnabled = createFlagFunction('enable-cancel-subscription', false);

export const isCanduEnabled = createFlagFunction('enable-candu', false);

export const isCaptchaEnabled = createFlagFunction('enable-captcha', true);

export const isCloneFlagEnabled = createFlagFunction('enable-clone-feature-flag', true);

/**
 * Use `!isDisableCodeReferencesWithOverrideEnabled() && isCodeReferencesEnabled()` when feature guarding is needed.
 */
export const isCodeReferencesEnabled = createFlagFunction('enable-flag-code-references', true);

export const isCompareCopyEnabled = createFlagFunction('enable-compare-and-copy', true);

export const isConcurrentlyFetchManifestsAndSubsEnabled = createFlagFunction(
  'concurrently-fetch-goaltender-manifests-and-subscriptions',
  false,
);

export const isContextKindGeneric = createFlagFunction('context-kinds-for-aggregation', false);

export const isCourierInboxEnabled = createFlagFunction('enable-courier-inbox', false);

export const isCustomRolesEnabled = createFlagFunction('custom.roles', true);

export const isDatadogRUMAppEnabled = createFlagFunction('enable-web-app-data-dog-rum-application-metrics', false);

export const isNewRelicRumEnabled = createFlagFunction('enable-web-app-new-relic-rum', false);

/**
 * Substring -> error group name. Collapses noisy JS errors that New Relic would
 * otherwise fragment into one group per URL, chunk id, or bundle hash, so they
 * can be ignored once in Errors Inbox. First match wins, so order the more
 * specific substrings first. Grouping only: nothing is dropped.
 */
export const newRelicErrorGroups = createFlagFunction<Array<{ match: string; group: string }>>(
  'web-app-new-relic-error-groups',
  [
    { match: 'Loading CSS chunk', group: 'Loading CSS chunk failed' },
    { match: 'Loading chunk', group: 'Loading chunk failed' },
    { match: "reading 'M_ID'", group: 'Browser extension' },
    { match: 'chrome-extension://', group: 'Browser extension' },
    { match: 'moz-extension://', group: 'Browser extension' },
    { match: 'accounts.google.com/gsi/client', group: 'Google Sign-In' },
    { match: 'GSI_LOGGER', group: 'Google Sign-In' },
    { match: 'Failed to fetch', group: 'Network request failed' },
    { match: 'Fetch is aborted', group: 'Network request aborted' },
    { match: 'signal is aborted without reason', group: 'Network request aborted' },
    { match: 'The user aborted a request', group: 'Network request aborted' },
    { match: 'AbortError', group: 'Network request aborted' },
    { match: 'csp_violation', group: 'CSP violation' },
    { match: "Failed to execute 'insertBefore' on 'Node'", group: 'DOM mutation by third party' },
    { match: "Failed to execute 'removeChild' on 'Node'", group: 'DOM mutation by third party' },
    { match: 'ResizeObserver loop', group: 'ResizeObserver loop' },
    { match: 'Script error', group: 'Script error (cross-origin)' },
    { match: 'NetworkError when attempting to fetch resource', group: 'Network request failed' },
  ],
);

export const isDataExportEnabled = () => configurableDestinations().length > 0;

/**
 * DON'T USE DIRECTLY - Override switch to disable code references.
 *
 * Use `!isDisableCodeReferencesWithOverrideEnabled() && isCodeReferencesEnabled()` when feature guarding is needed.
 */
export const isDisableCodeReferencesWithOverrideEnabled = createFlagFunction('disable-code-references', false);

export const isDownloadCsvButtonForExperimentExposuresEnabled = createFlagFunction(
  'enable-download-csv-button-for-experiment-exposures',
  false,
);

export const isEmailVerificationRequired = createFlagFunction('require-email-verification', false);

export const isEnableStripePromoCodesEnabled = createFlagFunction('enable-stripe-promo-codes', false);

export const isEnvironmentCreationEnabled = createFlagFunction('create.environments', false);

export const isEventDebuggerEnabled = createFlagFunction('event-debugger', true);

export const isExperimentAllocationEnabled = createFlagFunction('enable-experiment-allocation', false);

export const isExperimentApprovalsEnabled = createFlagFunction('enable-experiment-approvals', true);

export const isExperimentationApiEnabled = createFlagFunction('enable-experimentation-api', false);

export const isExperimentationForAiConfigsEnabled = createFlagFunction('enable-experimentation-for-ai-configs', false);

export const isExperimentationTreatmentImagesEnabled = createFlagFunction(
  'enable-experimentation-treatment-images',
  false,
);

export const isExperimentCommentsEnabled = createFlagFunction('enable-experiment-comments', false);

export const isFactorySettingsEnabled = createFlagFunction('enable-factory-settings', false);

export const isFeatureStoreIntegrationEnabled = createFlagFunction('enable-feature-store-integration', false);

export const isFeedbackSubscriptionEnabled = createFlagFunction('enable-qf-subscription-endpoints', false);

export const isFilterByApproversEnabled = createFlagFunction('enable-filter-by-approvers', false);

export const isFlagDescriptionEditInConfirmationModal = createFlagFunction(
  'flag-description-edit-in-confirmation-modal',
  false,
);

export const isFlagImportIntegrationEnabled = createFlagFunction('enable-flag-import-integration', true);

export const isFlagLinkPopoverEnabled = createFlagFunction('enable-flag-link-popover', false);

export const isFlagLinksEnabled = createFlagFunction('enable-flag-links', false);

export const isFlagPrerequisitesEnabled = createFlagFunction('Enable-flag-prerequisites', true);

export const isFlagStatusEnabled = createFlagFunction('read.feature.status.redis', true);

export const isGetBigSegmentUserTargetEndpointEnabled = createFlagFunction(
  'enable-get-unbounded-segment-user-target',
  false,
);

export const isGithubOAuthSignUpEnabled = createFlagFunction('enable-github-oauth-sign-up', false);

export const isGoogleOAuthSignUpEnabled = createFlagFunction('enable-google-oauth-sign-up', false);

export const isHoldoutArchiveEnabled = createFlagFunction('experiment-holdouts-allow-archive', false);

export const isHostedPaEnabled = createFlagFunction('is-hosted-pa-enabled', false);

export const isInitialPrereqsEnabled = createFlagFunction('enable-initial-prereqs', false);

export const isMemberAvatarUploadEnabled = createFlagFunction('enable-team-member-avatar-upload', false);

export const isMetricEventActivityTabEnabled = createFlagFunction('EnableMetricEventActivityTab', false);

export const isMFARedirectEnabled = createFlagFunction('enable-mfa-redirect', false);

export const isMobileAppTargetingEnabled = createFlagFunction('enable-mobile-app-targeting', false);

export const isNativeBigSegmentExportEnabled = createFlagFunction('enable-native-big-segment-export', false);

export const isNativeBigSegmentUIEnabled = createFlagFunction('enable-native-big-segment-ui', false);

export const isNewHiresAtornsiiEnabled = createFlagFunction('new-hires-add-atornsii', false);

export const isNewHiresExampleUserEnabled = createFlagFunction('new-hires-add-example-user', false);

export const isNewHiresFsareshwalaEnabled = createFlagFunction('new-hires-add-fsareshwala', false);

export const isNewHiresJchenEnabled = createFlagFunction('new-hires-add-jchen', false);

export const isNewHiresJliuEnabled = createFlagFunction('new-hires-add-jliu', false);

export const isNewHiresJmcelroyEnabled = createFlagFunction('new-hires-add-jmcelroy', false);

export const isNewHiresNBarthEnabled = createFlagFunction('new-hires-add-nbarth', false);

export const isNewHiresTAhlfeldEnabled = createFlagFunction('new-hires-add-tahlfeld', false);

export const isNewHiresRaskaraEnabled = createFlagFunction('new-hires-add-raskara', false);

export const isNewHiresJpeaseEnabled = createFlagFunction('new-hires-add-jpease', false);
export const isNewHiresUNwakucheEnabled = createFlagFunction('new-hires-add-unwakuche', false);

export const isNewHiresKongEnabled = createFlagFunction('new-hires-add-kong', false);

export const isNewHiresRAbdrashitovEnabled = createFlagFunction('new-hires-add-rabdrashitov', false);

export const isNewHiresSMarksonEnabled = createFlagFunction('new-hires-add-smarkson', false);

export const isNoAccessRoleEnabled = createFlagFunction('enable-no-access-role', false);

export const isPayloadFilteringEnabled = createFlagFunction('payload-filter-ui', false);

export const isPayloadFilteringTagSupportEnabled = createFlagFunction('payload-filter-ui-tag-support', false);

export const isPercentageRolloutForSegmentsEnabled = createFlagFunction(
  'enable-percentage-rollout-in-segment-rules',
  false,
);

/**
 * isPlanChangeEnabled is really telling us whether the instance (EU, Federal, Commercial) has access to Stripe or not
 */
export const isPlanChangeEnabled = createFlagFunction('plan.change', false);

export const isProductSwitcherEnabled = createFlagFunction('enable-product-switcher', false);

export const isProjectCreationEnabled = createFlagFunction('create-projects', false);

export const isReactQueryDevtoolsEnabled = createFlagFunction('enable-react-query-devtools', false);

export const isRebrandDiamondVariantEnabled = createFlagFunction('rebrand-diamond-variant', false);

export const isReleaseGuardianLeftNavMasterEnabled = createFlagFunction('rg-left-nav-master', false);

export const isRequireCommentsEnabled = createFlagFunction('enable-require-comments', true);

export const isRequiredApprovalsEnabled = createFlagFunction('require-approvals-for-an-environment', true);

export const isRequireReadyToArchiveBeforeArchivalEnabled = createFlagFunction(
  'require-ready-to-archive-before-archival',
  false,
);

export const isResetPasswordFormRefreshEnabled = createFlagFunction('reset-password-form-refresh', false);

export const isRichWebhookStatusEnabled = createFlagFunction('enable-rich-webhook-status', false);

export const isRuleExclusionEnabled = createFlagFunction('enable-rule-exclusion', false);

export const isSampleUsersListForPaUserActivityEnabled = createFlagFunction(
  'show-sample-users-list-for-pa-user-activity',
  false,
);

export const isScheduledFlagChangesEnabled = createFlagFunction('enable-scheduled-flag-changes', false);

export const isScimEnabled = createFlagFunction('enable-scim-endpoints', false);

export const isSeatLimitEnforced = createFlagFunction('enforce-seat-limits', false);

export const isSelfServeFromEnterpriseTrialAllowed = createFlagFunction(
  'allow-self-serve-from-enterprise-trial',
  false,
);

export const isSessionReplayInExperimentExposuresEnabled = createFlagFunction(
  'enable-session-replay-in-experiment-exposures',
  false,
);

export const isShowOnboardingInNavigationEnabled = createFlagFunction('show-onboarding-in-navigation', false);

export const isSkipToContentEnabled = createFlagFunction('skip-to-content', false);

export const isSlackIncomingHooksSupported = createFlagFunction('enable.slack.hooks', false);

export const isSlackTeamsBannerEnabled = createFlagFunction('enable-slack-teams-banner-on-integrations-page', false);

export const isSSOEnabled = createFlagFunction('sso-saml', false);

export const isStaticRelayProxyQueryingEnabled = createFlagFunction('usage-query-static-times', true);

export const isStoreViewSelectionInMemberPreferencesEnabled = createFlagFunction(
  'store-view-selection-in-member-preferences',
  false,
);

export const isStratifiedSamplingEnabled = createFlagFunction('enable-stratified-sampling', false);

export const isStripeCreditCardUpdateEnabled = createFlagFunction('update-stripe-packages-creditcard', false);

export const isSurfaceSdkMisconfigOnConnectEnabled = createFlagFunction('surface-sdk-misconfig-on-connect', false);

export const isSyncedSegmentRelayUIEnabled = createFlagFunction('synced-segment-relay-ui', false);

export const isTeamsEnabled = createFlagFunction('account.enable.teams', true);

export const isThirdPartyScriptsEnabled = createFlagFunction('enable-third-party-scripts', true);

export const isTrackEventsEnabled = createFlagFunction('enable-track-events', true);

export const isTriggerWorkflowsEnabled = createFlagFunction('enable-trigger-workflows', false);

export const isTypographyRefreshEnabled = createFlagFunction('typography-refresh', false);

export const isUpdateBigSegmentTargetsUIEnabled = createFlagFunction('enable-update-big-segment-targets-ui', false);

export const isUseHTTPMethodOverrideEnabled = createFlagFunction('use-http-method-override', false);

export const isVercelMarketplaceOauthSignUpEnabled = createFlagFunction(
  'enable-vercel-marketplace-oauth-sign-up',
  false,
);

export const isWorkflowBuilderEnabled = createFlagFunction('enable-workflow-builder', false);

export const isWorkflowBuilderUpsellEnabled = createFlagFunction('enable-workflow-builder-upsell', true);

export const launchInsights = createFlagFunction('launch-insights', false);

export const launchpadButtonExperiment = createFlagFunction<
  | 'off'
  | 'mono-flat'
  | 'mono-gradient'
  | 'mono-gradient-metal'
  | 'mono-gradient-lime'
  | 'mono-gradient-lime-double-strength'
  | 'mono-neumorphic'
  | 'lime-neumorphic'
  | 'lime-subtle'
  | 'lime-flat'
  | 'lime-dark-border'
  | 'lime-outline'
>('launchpad-button-experiment', 'off');

export const isLaunchpadExpandedHitAreaEnabled = createFlagFunction('launchpad-expanded-hit-area', false);

export const limitHistoricalGuardedReleasesTo90Days = createFlagFunction(
  'limit-historical-guarded-releases-to-90-days',
  false,
);

export const liveEventsClickhouse = createFlagFunction('live-events-clickhouse', false);

export const liveEventsClickhouseAutoUpdate = createFlagFunction('live-events-clickhouse-auto-update', false);

export const lookbackForTrendsPage = createFlagFunction('change-trends-page-lookback', 31);

export const maxAdaptiveTriggersPerResource = createFlagFunction('max-adaptive-triggers-per-resource', 50);

export const maximumAllowedMonitoredRolloutDurationMs = createFlagFunction(
  'maximum-allowed-monitored-rollout-duration-ms',
  -1,
);

export const maximumAllowedRolloutStageDurationMs = createFlagFunction('maximum-allowed-rollout-stage-duration', -1);

export const maxRollbackDays = createFlagFunction('max-rollback-days', 7);

// Maximum number of views that can be linked to an SDK key's custom payload. The default must
// match the backend default in internal/dogfood/raw_flags/flag_defs.go — the backend enforces
// this limit on create/update, the frontend mirrors it for pre-submit validation (REL-15141).
export const maxViewsPerFilteredPayload = createFlagFunction('max-views-per-filtered-payload', 10);

export const memberFilterPageSize = createFlagFunction('member-filter-page-size', 20);

export const metricDataSourceValueColumnOptional = createFlagFunction(
  'metric-data-source-value-column-optional',
  false,
);

export const metricKindsSupportedOnPA = createFlagFunction<{ supported_metrics: string[] }>(
  'metric-kinds-supported-on-pa',
  { supported_metrics: ['custom'] },
);

export const metricsBannerVariations = createFlagFunction<{
  headline: string;
  description: string;
  useAlert?: boolean;
}>('metric-banner-variations-mab', {
  headline: 'Get real-time insights with feature flag monitoring',
  description:
    'Initialize the Observability plugin to get real-time visibility into errors, performance, and web vitals. Ship new features and immediately see their impact.',
  useAlert: false,
});

export const metricCreateModalSections = createFlagFunction('metric-create-modal-sections', false);

export const metricEventFiltersQueryBuilder = createFlagFunction('metric-event-filters-query-builder', false);
export const enableWarehouseNativeMetricFilters = createFlagFunction('enable-warehouse-native-metric-filters', false);

export const metricWindowingFrontend = createFlagFunction('metric-windowing-frontend', false);

export const migrateContextKindForm = createFlagFunction('migrate-context-kind-form', false);

export const migrateContextsEpicToReactQuery = createFlagFunction('migrate-contexts-epic-to-react-query', false);

export const migrateToAgentControl = createFlagFunction('migrate-to-agentcontrol', false);

export const miniEvalChartFlagTargetingPage = createFlagFunction('mini-eval-chart-flag-targeting-page', false);

export const nativeExperimentationFormWrapperKinds = createFlagFunction<string[]>(
  'native-experimentation-form-wrapper',
  [],
);

export const newAccountTrialDurationDays = createFlagFunction('new-account-trial-duration-days', 14);

export const newPAOnboardingFlowEnabled = createFlagFunction('new-onboarding-flow-enabled', false);

export const nonEnterpriseRoles = createFlagFunction('non-enterprise-roles', true);

export const normalizeFlagEvaluationVariationOrder = createFlagFunction(
  'normalize-flag-evaluation-variation-order',
  false,
);

export const oneClickInstallSdk = createFlagFunction('one-click-install-sdk', false);
export const o11yPaEventsTrackName = createFlagFunction('o11y-pa-events-track-name', false);

export const observabilityAccess = createFlagFunction('observability-access', 'disabled');

// Selects the session timeline hover-preview mode:
// - 'off':                    no hover preview
// - 'local-rrweb':            an in-browser rrweb mini-player that seeks to the hovered timestamp
// - 'server-side-screenshots': server-rendered preview frames (the legacy pipeline)
// A dedicated multivariate (string) flag. LaunchDarkly can't change an existing flag's variation
// type, so the legacy boolean `enable-session-preview-screenshots` couldn't be turned into a 3-way
// flag; that boolean is left untouched (off everywhere) and this replaces it. Defaults to 'off' so
// an absent/unreachable flag is a no-op (no preview).
export const observabilitySessionPreviewMode = createFlagFunction<'off' | 'local-rrweb' | 'server-side-screenshots'>(
  'observability-session-timeline-preview-mode',
  'off',
);

export const observabilitySessionsUnavailable = createFlagFunction('o11y-sessions-unavailable', false);

export const observabilitySetupBaseDomain = createFlagFunction(
  'observability-setup-base-domain',
  'observability.app.launchdarkly.com',
);

export const observabilitySetupHideSessionReplay = createFlagFunction('observability-setup-hide-session-replay', false);

export const offerExperimentSimpleMode = createFlagFunction('offer-experiment-simple-mode', false);

export const offlineEvalAdminTokenLimits = createFlagFunction('offline-eval-admin-token-limits', false);

export const offlineEvaluationResultsRefetchInterval = createFlagFunction(
  'offline-evaluation-results-refetch-interval',
  2000,
);

export const openSeatsNotificationLimit = createFlagFunction('open-seats-notification-limit', 0);

export const outOfTheBoxRolesReleasePhase = createFlagFunction('ootb-roles-release-phase', 0);

export const paChartsDefaultDateFilter = createFlagFunction('pa-charts-default-date-filter', {
  unit: 'day',
  value: 3,
});

export const persistChromeSidebarState = createFlagFunction('persist-chrome-sidebar-state', false);

export const persistFlagListQuery = createFlagFunction('persist-flag-list-query', false);

export const planPickerProminentMonthly = createFlagFunction('plan-picker-prominent-monthly', false);

export const planUsageTabsOverride = createFlagFunction<{
  [tabKey in
    | 'serviceConnectionsTab'
    | 'clientSideMAUTab'
    | 'experimentationKeysTab'
    | 'experimentationEventsTab'
    | 'experimentationMAUTab'
    | 'dataExportTab'
    | 'dataExportCombinedTab'
    | 'warehouseDataExportTab'
    | 'observabilitySessionsTab'
    | 'observabilityErrorsTab'
    | 'observabilityLogsTab'
    | 'observabilityTracesTab'
    | 'observabilityMetricsTab'
    | 'vegaAITab'
    // Legacy override key for the AI Runs tab; predates the AI executions -> AI runs rename.
    | 'aiExecutionsTab'
    | 'totalContextsTab'
    | 'clientSideContextsTab'
    | 'serverSideContextsTab'
    | 'clientConnectionsTab'
    | 'peakConcurrentConnectionsTab']: Array<
    'planEnabled' | 'planDisabled' | 'diagnosticEnabled' | 'diagnosticDisabled'
  >;
}>('plan-usage-tabs-override', {
  serviceConnectionsTab: [],
  clientSideMAUTab: [],
  experimentationKeysTab: [],
  experimentationEventsTab: [],
  experimentationMAUTab: [],
  dataExportTab: [],
  dataExportCombinedTab: [],
  warehouseDataExportTab: [],
  observabilitySessionsTab: ['planDisabled', 'diagnosticDisabled'],
  observabilityErrorsTab: ['planDisabled', 'diagnosticDisabled'],
  observabilityLogsTab: ['planDisabled', 'diagnosticDisabled'],
  observabilityTracesTab: ['planDisabled', 'diagnosticDisabled'],
  observabilityMetricsTab: ['planDisabled', 'diagnosticDisabled'],
  vegaAITab: ['planDisabled', 'diagnosticDisabled'],
  aiExecutionsTab: ['planDisabled', 'diagnosticDisabled'],
  totalContextsTab: [],
  clientSideContextsTab: [],
  serverSideContextsTab: [],
  clientConnectionsTab: [],
  peakConcurrentConnectionsTab: [],
});
export const playgroundAdoptRunningEvaluation = createFlagFunction('playground-adopt-running-evaluation', false);

export const playgroundDefaultSystemMessages: {
  column1: string;
  column2: string;
} = {
  column1: 'You are a helpful assistant.',
  column2: 'You are an expert assistant.',
};

export const playgroundDefaultUserMessage = createFlagFunction(
  'playground-default-user-message',
  'Explain how AI configs work in one sentence',
);

/**
 * Row-count threshold at or above which running all rows of a dataset prompts a confirmation modal.
 * A negative value (the default) disables the modal entirely.
 */
export const playgroundLargeDatasetRowCountThreshold = createFlagFunction(
  'playground-large-dataset-row-count-threshold',
  -1,
);

/**
 * Estimated playground input-cost score threshold for the pre-run confirmation warning.
 * LD variations are numeric (e.g. "Default Limit" = 5000, "No Limit" = -1, plus any custom values).
 * A negative value disables the cost warning; any non-negative value is the cutoff.
 */
export const playgroundInputTokenCostThreshold = createFlagFunction('playground-input-token-cost-threshold', -1);

export const productAnalyticsAccess = createFlagFunction<'enabled' | 'disabled' | 'waitlist'>(
  'product-analytics-access',
  'disabled',
);

export const productAnalyticsFeatureMatrix = createFlagFunction<ProductAnalyticsFeatureMatrix>(
  'pa-feature-matrix',
  defaultFeatureMatrixValue,
);

export const preserveExperimentFormValuesAfterSave = createFlagFunction(
  'preserve-experiment-form-values-after-save',
  false,
);

export const preserveUnrecognizedModelParams = createFlagFunction('preserve-unrecognized-model-params', false);

export const productChangelogUrl = createFlagFunction('product-changelog-url', 'https://whatsnew.launchdarkly.com/');

export const projectSwitcherInternalGetEndpoint = createFlagFunction('project-switcher-internal-get-endpoint', false);

export const projectSwitcherResetEnvironments = createFlagFunction('project-switcher-reset-environments', false);

export const qualitativeFeedbackGithubAppLinks = createFlagFunction('qualitative-feedback-github-app-links', false);

export const qualitativeFeedbackGithubPrLink = createFlagFunction('qualitative-feedback-github-pr-link', false);

export const qualitativeFeedbackVegaIntegration = createFlagFunction('qualitative-feedback-vega-integration', false);

export const qualitativeFeedbackVersion = createFlagFunction('qualitative-feedback-version', 'disabled');

export const racAddClauseButton = createFlagFunction('rac-add-clause-button', false);

export const racAddRuleButton = createFlagFunction('rac-add-rule-button', false);

export const racClauseSelectors = createFlagFunction('rac-clause-selectors', false);

export const racHoldoutStatusFilter = createFlagFunction('rac-holdout-status-filter', false);

export const racSegmentKindFilter = createFlagFunction('rac-segment-kind-filter', false);

export const racTagFilter = createFlagFunction('rac-tag-filter', false);

export const racTimeRangeFilter = createFlagFunction('rac-time-range-filter', false);

export const readPercentileResultsFromClickhouse = createFlagFunction('read-percentile-results-from-clickhouse', false);

export const regressionMarkerTime = createFlagFunction('regression-marker-time', false);

export const rejectEmptySegmentMatchClauses = createFlagFunction('reject-empty-segment-match-clauses', false);

export const releaseAddFlagToCodeV3 = createFlagFunction('release-add-flag-to-code-v3', false);

export const releaseBuilderAutoLaunch = createFlagFunction('release-builder-auto-launch', false);

export const releaseBuilderCtaCopyExperiment = createFlagFunction<'Release' | 'Ship it'>(
  'release-builder-cta-copy-experiment',
  'Ship it',
);

export const releaseComputedSegmentInstructions = createFlagFunction('computed-segment-instructions', false);

export const releaseGuardian = createFlagFunction('release-guardian', false);

export const releaseInternalGetApplicationsByProject = createFlagFunction(
  'release-internal-get-applications-by-project',
  false,
);

export const releaseListAPIMaxLimit = createFlagFunction('release-list-api-max-limit', 20);

export const releaseSegmentsV2 = createFlagFunction('release-segments-v2', false);

export const releaseUnifiedFormSubmit = createFlagFunction<Record<string, boolean>>('release-unified-form-submit', {});

export const removeStartEnterpriseTrialButtonFromUpsellModal = createFlagFunction(
  'remove-start-enterprise-trial-button-from-upsell-modal',
  false,
);

export const rememberMaintainerSelectionAtFlagCreation = createFlagFunction(
  'remember-maintainer-selection-at-flag-creation',
  false,
);

export const resourceCountEnvironments = createFlagFunction('resource-count-environments', 300);

export const resourceCountReleasePipelines = createFlagFunction('resource-count-release-pipelines', 1);

export const restoreEnvCanceledNavigation = createFlagFunction('restore-env-selector-canceled-navigation', false);

export const reviveFlagOverviewAcrossEnvironments = createFlagFunction(
  'revive-flag-overview-across-environments',
  false,
);

export const rgStatisticalConfidenceThreshold = createFlagFunction('rg-statistical-confidence-threshold', 95);

export const rightSidebarShowActionIcons = createFlagFunction('right-sidebar-show-action-icons', false);

export const rolloutBucketInWorkflowBuilder = createFlagFunction('rollout-bucket-in-workflow-builder', true);

export const sdkActiveMocking = createFlagFunction<'disabled' | 'enabled' | 'true' | 'false'>(
  'sdk-active-mocking',
  'disabled',
);

export const sdkKeysLimit = createFlagFunction('max-sdk-keys-per-environment', 50);

export const sdkSetupAgentControlLink = createFlagFunction('sdk-setup-agent-control-link', false);

export const sdkSetupIncentiveVariant = createFlagFunction<
  | {
      tagline: string;
      body: string;
      cta: string;
      link: string;
      label: string;
    }
  | Record<string, never>
>('incentive-feedback-variant', {});

export const segmentKeyLimit = createFlagFunction('segment-key-limit', 20);

export const isSegmentScheduledChangesEnabled = createFlagFunction('segment-scheduled-changes', false);

export const segmentTargetBatchLimit = createFlagFunction('target-batch-limit', -1);

export const selfServeCheckoutRecordedTermsVersion = createFlagFunction(
  'self-serve-checkout-recorded-terms-version',
  '2024-07-07',
);

export const selfServeEnterpriseTrialDuration = createFlagFunction('self-service-enterprise-trial-duration', 30);

export const sentryIntegration = createFlagFunction('sentry-integration', false);

export const shouldCleanupAfterFlagDeletion = createFlagFunction(
  'cleanup-flag-related-resources-after-flag-deletion',
  false,
);

export const shouldClearLocalStorageOnLogout = createFlagFunction('clear-localstorage-on-logout', false);

export const shouldSeeGuardedRolloutsUpsell = createFlagFunction('guarded-releases-upsell', false);

export const shouldSendFrontEndTrackEventsToCatfood = createFlagFunction(
  'send-front-end-track-events-to-catfood',
  false,
);

export const shouldShowPAChartDescription = createFlagFunction('show-chart-description', false);

export const shouldTrackWebVitals = createFlagFunction<string[]>('track-web-vitals', []);

export const showAiConfigsSdkSetupBanner = createFlagFunction('show-ai-configs-sdk-setup-banner', false);

export const showApiVersionDeprecationWarning = createFlagFunction('show-api-version-deprecation-warning', false);

export const showAwsMarketplaceSignInLink = createFlagFunction('show-aws-marketplace-sign-in-link', false);

export const showDataExportSendTestEvent = createFlagFunction('show-data-export-send-test-event-field', false);

export const showLifecycleCleanupPreview = createFlagFunction('show-lifecycle-cleanup-preview', false);

export const showLifecycleCleanupPreviewFeaturePreview = createFlagFunction(
  'show-lifecycle-cleanup-preview-feature-preview',
  false,
);

export const showEstimatedEvaluationRunCost = createFlagFunction('show-estimated-evaluation-run-cost', false);

export const showExpandedEvaluationRunCriteriaStatusBadge = createFlagFunction<
  Array<'run-summary-page' | 'evaluation-runs-list' | 'results-table-header'>
>('show-expanded-evaluation-run-criteria-status-badges', ['run-summary-page']);

export const showExperimentDesignActiveFeedbackForm = createFlagFunction(
  'show-experiment-design-active-feedback-form',
  false,
);

export const showExperimentSummary = createFlagFunction('show-experiment-summary', false);

export const showHourDelayUsageMessage = createFlagFunction('show-hour-delay-usage-message', true);

export const showImportantAuditLogEntries = createFlagFunction('show-important-audit-log-entries', false);

export const showNewDateAndCohortFilterLayout = createFlagFunction('date-and-cohort-filter-layout', false);

export const showNewExperimentCreateOptionsInFlagRules = createFlagFunction(
  'enable-experiment-creating-in-new-areas-of-flag-rules',
  false,
);

export const showSettingsAccessSection = createFlagFunction('show-settings-access-section', true);

export const showSettingsPlansAndUsageSection = createFlagFunction('show-settings-plans-and-usage-section', true);

export const showVariablesColumnInBulkEvalResults = createFlagFunction(
  'show-variables-column-in-bulk-eval-results',
  false,
);
export const showViewMembershipInAddResourcesModal = createFlagFunction(
  'show-view-membership-in-add-resources-modal',
  false,
);
export const sidebarMetricInstrumentation = createFlagFunction('sidebar-metric-instrumentation', false);
export const signUpPagePromoBanner = createFlagFunction<'none' | 'statsig-migration-offer'>(
  'sign-up-page-promo-banner',
  'none',
);
export const slottedFlagSearch = createFlagFunction('slotted-flag-search', false);

export const spaPageViewsForSegmentCom = createFlagFunction('spa-page-views-for-segment-com', false);

export const specificOverageAlerts = createFlagFunction<string[]>('enable-specific-overage-alerts', []);

export const standardMetricGroupMetricLimit = createFlagFunction('standard-metric-group-metric-limit', 20);

export const supportBayesianPercentiles = createFlagFunction('support-bayesian-percentiles', false);

export const supportedHostedPAContextKind = createFlagFunction('hosted-pa-user-identifier-context-kind', 'user');

export const supportedWarehousesForRatioAndDua = createFlagFunction<string[]>(
  'supported-warehouses-for-ratio-and-dua',
  [],
);

export const switchBillingYearlyToMonthlyGracePeriod = createFlagFunction(
  'switch-billing-yearly-to-monthly-grace-period',
  0,
);

export const syncedSegmentRelayVersion = createFlagFunction('synced-segment-relay-version', '6.99.0');

export const targetingChangeVisualization = createFlagFunction<{
  highlights: boolean;
  badges: boolean;
  diff: boolean;
}>('enable-targeting-change-visualization', {
  highlights: false,
  badges: false,
  diff: false,
});

export const teamMaintainerRequirementUITreatment = createFlagFunction<
  'first-class' | 'footer-tag' | 'footer-tag-error-line'
>('team-maintainer-requirement-ui-treatments', 'footer-tag');

export const teamsListPageSize = createFlagFunction('teams-list-page-size', 20);

export const throwErrorsInRouterHooks = createFlagFunction('throw-errors-in-router-hooks', false);

export const trackRecentProjects = createFlagFunction('track-recent-projects', false);

export const tryItLiveExitNudgeExperiment = createFlagFunction('try-it-live-exit-nudge-experiment', false);
/**
 * Which palette entry the LaunchDarkly TUI draws its focus cursor in — see `packages/ldtui/src/cursorColor.ts`.
 *
 * `''` rather than a variation as the default, deliberately: an account without this flag keeps the cursor colour the
 * TUI has always used, so "the flag is not set up" and "the flag says 1" are different states rather than the same one.
 */
export const tuiCursorColor = createFlagFunction<'' | '1' | '2' | '3' | '4'>('tui-cursor-color', '');
/**
 * Whether the LaunchDarkly TUI shows its Atlassian/Blog feature — the Blogs tab, the onboarding Atlassian step, and the
 * Atlassian MCP recommendation — see `packages/ldtui/src/atlassianAvailable.ts`.
 *
 * `true` rather than `false` as the default, deliberately: an account without this flag keeps the Blogs tab and Atlassian
 * offers the TUI has always shown, so "the flag is not set up" and "the flag says available" resolve the same way, and
 * only an explicit `Hidden` variation removes them — the same reasoning as `tuiAgentControl`.
 */
export const tuiAtlassian = createFlagFunction<boolean>('tui-atlassian', true);
/**
 * Whether the LaunchDarkly TUI shows its AgentControl tab and the features behind it — see
 * `packages/ldtui/src/agentControlAvailable.ts`.
 *
 * `true` rather than `false` as the default, deliberately: an account without this flag keeps the AgentControl tab the
 * TUI has always shown, so "the flag is not set up" and "the flag says available" resolve the same way, and only an
 * explicit `Hidden` variation removes the tab.
 */
export const tuiAgentControl = createFlagFunction<boolean>('tui-agentcontrol', true);
/**
 * Whether the LaunchDarkly TUI shows its GitHub tab — the pull request list, its filters and its detail page — see
 * `apps/ld-tui/src/githubAvailable.ts`.
 *
 * `false` rather than `true` as the default, which is the opposite of `tuiAtlassian` and `tuiAgentControl` above and
 * deliberately so. Those two default to shown because the tabs they gate existed before the flags did, so an account
 * without the flag should look exactly as it did rather than lose a tab on upgrade. This tab is new: nothing is taken
 * away by defaulting it off, and "the flag is not set up" and "the flag says hidden" resolve the same way — which is
 * also what keeps the tab strip's width the eight tabs every existing probe was written against.
 */
export const tuiGithub = createFlagFunction<boolean>('tui-github', false);
/**
 * Whether the LaunchDarkly TUI shows its Scans tab — the archived repository scans, their detail page, and the
 * `Scan repository` action that runs a new one — see `apps/ld-tui/src/scansAvailable.ts`.
 *
 * `false` for `tuiGithub`'s first reason and a second of its own. The first: this tab is new, so nothing is taken
 * away by defaulting it off, and "the flag is not set up" and "the flag says hidden" resolve the same way. The
 * second is sharper than GitHub's, because this tab goes in at **position two** rather than last-but-Settings —
 * turning it on renumbers six tabs and moves the strip's full-label threshold from 94 columns to 102, which
 * fifteen probe mounts sit either side of. Defaulting off is what keeps every one of them true.
 */
export const tuiScans = createFlagFunction<boolean>('tui-scans', false);
/**
 * Whether the LaunchDarkly TUI runs status line plugins — a shell command from the user's config whose stdout is drawn
 * as extra rows above the chat pane's own status line — see `apps/ld-tui/src/statusLinePluginsAvailable.ts`.
 *
 * `false` rather than `true` as the default, for the reason `tuiGithub` above gives and one more that is specific to
 * this flag. A plugin is an arbitrary shell command, which the compiled TUI can only spawn because `sh` is in its
 * `--allow-run` list; defaulting off keeps that capability unreachable until somebody deliberately turns this on, so
 * granting the permission and shipping the feature stay two separate decisions.
 *
 * This flag is the *only* switch. There is deliberately no environment-variable override beside it: one existed
 * briefly, and a second way to turn on a capability whose whole point is that turning it on is a considered act is a
 * second thing to audit and a second thing to forget about.
 */
export const tuiStatusLine = createFlagFunction<boolean>('tui-status-line', false);
/**
 * Whether the LaunchDarkly TUI offers `Copy as JSON` and `Copy as TXT` in the chat's usage menu — the two rows that
 * put the whole request behind the latest turn on the clipboard, system prompt and tool schemas included, one
 * verbatim and one rendered for a person. See `apps/ld-tui/src/copyContextAvailable.ts`.
 *
 * `false` rather than `true` as the default, for the reason `tuiGithub` and `tuiStatusLine` above give: the row is new,
 * so nothing is taken away by defaulting them off and "the flag is not set up" and "the flag says hidden" resolve
 * the same way. It also keeps the usage menu the three rows every existing probe was written against.
 *
 * What it gates is a *copy*, not a capability: the request is recorded either way — see `sentContext.ts`, which one
 * send path writes to and only this row reads. So turning the flag on exposes nothing the session had not already
 * assembled and sent, and turning it off takes the row away rather than changing what the chat does.
 */
export const tuiCopyContext = createFlagFunction<boolean>('tui-copy-context', false);
export const trialDataSeedingMode = createFlagFunction<'off' | 'frontend' | 'backend'>('trial-data-seeding', 'off');
export const unauthenticatedPagePromo = createFlagFunction<
  | 'off'
  | 'galaxy-2025'
  | 'reinvent-2025'
  | 'agent-control-launch-2026'
  | 'hands-on-with-agentcontrol'
  | 'control-panel-2026'
  | 'runtime-lab-banner'
>('unauthenticated-page-promo', 'off');
export const useDocsSearchIndex = createFlagFunction('use-launchdarkly-com-docs-search-index', false);

export const userTargetAlertThreshold = createFlagFunction('user-target-alert-threshold', -1);

export const userTargetWarningThreshold = createFlagFunction('user-target-warning-threshold', -1);

export const utilizeDataVizForExperimentExposuresChart = createFlagFunction(
  'use-dataviz-for-experiment-exposures',
  false,
);

export const utilizeKeysForCompositeEvents = createFlagFunction('use-keys-for-composite-events', false);

export const validateNoSummedMetricsInFunnels = createFlagFunction('validate-no-summed-metrics-in-funnels', false);

export const vegaArtifactGraph = createFlagFunction('vega-artifact-graph', false);

export const vegaConversationJsonRendering = createFlagFunction('vega-conversation-json-rendering', false);

export const vegaConversationSidebar = createFlagFunction('vega-conversation-sidebar', false);

export const vegaCopyConversation = createFlagFunction('vega-copy-conversation', false);

export const vegaConversationTranscriptRedesign = createFlagFunction('vega-conversation-transcript-redesign', false);

export const vegaLandingPage = createFlagFunction('vega-landing-page', false);

export const vegaNavLink = createFlagFunction('vega-nav-link', false);

export const vegaTermsAcceptanceMode = createFlagFunction<'self-serve-form' | 'order-form-required' | 'disabled'>(
  'vega-terms-acceptance-mode',
  'disabled',
);

export const vegaWorkflows = createFlagFunction('vega-workflows', false);

export const vegaWorkspace = createFlagFunction('vega-workspace', false);

export const viewAuditlogQueryingSupport = createFlagFunction('view-auditlog-querying-support', false);

export const viewsAllowExpandingResourceMaintainers = createFlagFunction(
  'views-allow-expanding-resource-maintainers',
  false,
);
export const viewsMaxResourcesToLinkIncrementally = createFlagFunction('views-max-resources-to-link-incrementally', 20);
export const warehouseHostedMetricsSupportPercentiles = createFlagFunction(
  'warehouse-hosted-metric-support-percentiles',
  false,
);
export const warehouseWithDataExportSupport = createFlagFunction(
  'pa-warehouse-with-data-export',
  warehouseWithDataExportSupportDefaultValue,
);
export const welcomeSurveyInviteApiKeyOwner = createFlagFunction('welcome-survey-invite-api-key-owner', false);
export const welcomeSurveyInviteCoder = createFlagFunction('welcome-survey-invite-coder', false);
export const welcomeSurveyRotatingAiQuestion = createFlagFunction<string>(
  'welcome-survey-rotating-ai-question',
  'none',
);
export const welcomeSurveyRotatingQuestion = createFlagFunction<string>('welcome-survey-rotating-question', 'none');
export const welcomeSurveyRotatingQuestionBank = createFlagFunction<
  | Record<
      string,
      {
        questionText: string;
        inputType: 'single-select' | 'multi-select' | 'free-text';
        options?: string[];
        showWhenFirstAction?: string[];
      }
    >
  | { enabled: false }
>('welcome-survey-rotating-question-bank', { enabled: false });
export const winsorizationFrontendConfig = createFlagFunction<Record<string, boolean>>(
  'winsorization-frontend-config',
  {},
);
// Shared with gonfalon's backend and Orchestra, which only honor a configured
// reloadCadenceMinutes when this is on. The FE shows the refresh frequency row
// under the same key so users cannot set a cadence the compute path ignores.
export const wneCustomReloadCadence = createFlagFunction('wne-custom-reload-cadence', false);
export const wneMetricConfigUnification = createFlagFunction('wne-metric-config-unification', false);
export const wneReloadRampUp = createFlagFunction<{ cadenceMinutes?: number; windowMinutes?: number }>(
  'wne-reload-ramp-up',
  {},
);
export const isWneReloadRampUpEnabled = () => {
  const { cadenceMinutes, windowMinutes } = wneReloadRampUp() ?? {};
  return (
    Number.isInteger(cadenceMinutes) &&
    Number.isInteger(windowMinutes) &&
    cadenceMinutes !== undefined &&
    windowMinutes !== undefined &&
    cadenceMinutes > 0 &&
    windowMinutes > 0 &&
    cadenceMinutes % 15 === 0 &&
    cadenceMinutes <= windowMinutes
  );
};
export const workflowTemplatesModalRefresh = createFlagFunction('workflow-templates-modal-refresh', false);
// Shared with the warehouse-task-runner (event-recorder repo) and gonfalon's backend: N caps
// sliced warehouse experiment results at the top N dimension values per attribute key. On the FE
// it gates the warehouse Add Attributes modal (catalog + free-text) so unrestricted attribute
// addition only unlocks once compute is capped. 0 (also the fail-safe fallback) = off everywhere.
export const wtrTopNDimensionValues = createFlagFunction('wtr-topn-dimension-values', 0);

export const enableAppRestartBanner = createFlagFunction('enable-app-restart-banner', true);
export const enablePerceivableDisabledTooltipTriggers = createFlagFunction(
  'enable-perceivable-disabled-tooltip-triggers',
  false,
);
export const appRestartBannerData = createFlagFunction<{ enabled: boolean; text: string }>('app-restart-banner-data', {
  enabled: false,
  text: '',
});
